// src/pages/ChatPage.js — v3 (mockup-aligned, light theme, 3-col layout)
//
// Esta página agora envelopa duas funcionalidades que antes viviam em
// arquivos separados:
//   - ConversationsList (lista de conversas por chip) → coluna esquerda
//   - ChatPage v2 (chat) → coluna central
//
// E adiciona uma terceira coluna opcional (template panel) que abre
// quando o usuário entra no modo multi-select + clica em "Enviar template".
//
// O QUE SE PRESERVOU da implementação anterior:
//   - Conexão WebSocket com heartbeat 25s + reconexão exponencial
//   - Polling fallback de 10s pra cobrir multi-worker no backend
//   - Detecção de mensagens INBOUND novas via snapshot (clientsRef) +
//     Set de dedup (seenInboundIdsRef) — evita badge fantasma
//   - Scroll inteligente: só rola pro fim em (a) troca de cliente OU
//     (b) msg nova E user perto do fim. Histórico do scroll respeitado.
//   - Unread badge persistido em localStorage por device
//   - Refs anti-closure-stale (selectedClientRef, fetchClientsRef,
//     fetchMessagesRef) pra que os handlers do WS sempre usem valores
//     atuais
//   - Envio de mensagem otimista (placeholder local antes do ack)
//   - Templates dinâmicos da Meta API
//   - Áudio (PTT), upload de mídia, video call (Jitsi)
//
// O QUE É NOVO:
//   - Layout 3-col baseado no mockup (light theme, paleta brand verde)
//   - Multi-select de conversas com checkboxes
//   - Bulk action bar que aparece quando ≥1 conversa selecionada
//   - Panel direito de templates pra envio em massa (Promise.allSettled
//     dispara N requests em paralelo e mostra resumo)
//   - Filtros por status (Todas / Não lidas / Aguardando / Templates)
//
// O QUE SAIU:
//   - Sidebar collapse manual do chat (a TopBar+AppSidebar já cuidam disso)
//
// DARK MODE (set/26): voltou. A v3 tinha tirado o toggle alegando "menos
// código pra manter" — e de fato manter DOIS objetos de paleta em JS seria
// isso. A volta custou quase nada porque a paleta virou `var(--ch-*)`
// (ChatPage.css): trocar de tema é trocar um atributo no <html>, o React
// não re-renderiza nada e nenhum `sx` deste arquivo precisou mudar.
//
// REDESIGN (out/26, Claude Design "Chat Cetto.dc.html", telas 1b/1d/1f/1g):
//   - Fontes: Syne nos nomes e títulos, Outfit no resto. O `TEMA_FONTES`
//     envolve a página — sem ele o Typography do MUI cai na Roboto.
//   - Lista: filtros numa faixa com setas < > (`chat/FaixaRolavel`), linhas
//     arredondadas, seleção por círculo; no celular, toque longo entra no
//     modo de seleção (o círculo só aparece nele).
//   - Conversa: bolhas AGRUPADAS (3 px entre seguidas do mesmo lado, só a
//     última com o "rabinho" e o avatar), hora e risquinhos DENTRO da bolha,
//     a nossa em verde translúcido (`--ch-ob*`), áudio próprio
//     (`chat/AudioBolha`, com velocidade), separador Hoje/Ontem.
//   - Topo: o "i" (anotações) e o "Abrir no CRM" viraram UM botão de pessoa
//     que abre a mini-página do contato (`chat/PainelContato`) na coluna da
//     direita — a mesma dos templates, um de cada vez, templates primeiro. A
//     escolha (aberta/fechada) fica em `cetto:chat-contato`. As etiquetas
//     moram nela; no topo só aparecem com a coluna da direita fechada.
//   - Caixa de mensagem em pílula; no celular as ferramentas vão pro "+", o
//     campo tem 16 px (o iOS dá zoom abaixo disso) e templates e contato
//     sobem como folha de baixo. Antes o painel de templates nem aparecia
//     no celular.
//   - Cor só por token `--ch-*`, inclusive diálogos e o popover de
//     etiquetas: hex num `sx` daqui não acompanha o escuro.
//
// RESPONDER E ENCAMINHAR (nov/26):
//   - Responder: Reagir/Responder/Encaminhar aparecem no hover da bolha
//     (computador); no celular, o "⋯" ou o toque longo abrem a folha de
//     ações. "Respondendo a…" fica acima da caixa (Esc ou X cancela) e TODO
//     envio livre (texto, mídia, áudio, arquivo) leva `reply_to`; template
//     não cita. A citação das mensagens que já existem vem PRONTA do
//     servidor (`citacao` em cada mensagem); a da bolha otimista e a da
//     barra saem de `chat/citacao.js`, que repete `services/citacao.resumo`.
//     Clicar na citação rola até a original (`#msg-{id}`) e a destaca.
//   - Encaminhar: `chat/EncaminharDialog`, até 5 conversas, só as com a
//     janela de 24h aberta (`/messages/forward/janelas`). No WhatsApp do
//     cliente chega como mensagem nova — a API não marca "Encaminhada". O
//     selo "Encaminhada" aparece só aqui na Cetto: na que nós encaminhamos
//     e na que o cliente encaminhou pra nós (`context.forwarded`).
//   - Cartão de contato recebido vira `chat/CartaoContato`, com "Conversar"
//     abrindo a nova conversa já preenchida.
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, TextField, CircularProgress, Tooltip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme, Menu, MenuItem, Drawer, ThemeProvider, createTheme, Snackbar,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FiSearch, FiPaperclip, FiSend, FiMic,
  FiMessageSquare, FiVideo, FiCheckCircle, FiX, FiTrash2,
  FiFileText, FiSmile, FiExternalLink, FiCpu,
  FiSmartphone, FiAlertTriangle, FiPhone, FiClipboard,
  FiChevronDown, FiUser, FiPlus, FiCamera, FiImage, FiMail, FiClock, FiCheck, FiArrowUp,
  FiCornerUpLeft, FiCornerUpRight, FiMoreHorizontal,
} from 'react-icons/fi';
import { IoCheckmarkDoneOutline, IoCheckmarkOutline } from 'react-icons/io5';
import { BsPinAngle, BsPinAngleFill } from 'react-icons/bs';
import api from '../services/Api';
import {
  BITRATE_AUDIO, MAX_SEGUNDOS_AUDIO, formatarDuracao, validarArquivo,
} from '../services/midia';
import AppSidebar from '../components/AppSidebar';
import TopBar from '../components/TopBar';
import AISuggestionCard from '../components/AISuggestionCard';
import TemplateVarsDialog from '../components/TemplateVarsDialog';
import LimpezaConversasDialog from '../components/LimpezaConversasDialog';
import PainelEmoji from '../components/PainelEmoji';
import EnviarFormulario from './formularios/EnviarFormulario';
import RespostasLista from './formularios/RespostasLista';
import { novoLoteId } from '../utils/lote';
import ProtecaoDisparo, { bloqueioDoDisparo } from '../components/ProtecaoDisparo';
import QualificarNoChat from '../components/qualificacao/QualificarNoChat';
import FaixaRolavel from '../components/chat/FaixaRolavel';
import PainelContato from '../components/chat/PainelContato';
import AudioBolha from '../components/chat/AudioBolha';
import CartaoContato from '../components/chat/CartaoContato';
import EncaminharDialog from '../components/chat/EncaminharDialog';
import { citacaoLocal, podeEncaminhar, podeResponder, resumo as resumoDaMensagem } from '../components/chat/citacao';
import { formatarFone } from './UserAdmin/crm/theme';
import './ChatPage.css';

// ─── Paleta do chat ─────────────────────────────────────────────────────────
// Os valores viraram `var(--ch-*)` (definidos em ChatPage.css, dois jogos:
// claro e `[data-theme="dark"]`). A FORMA do objeto não mudou de propósito:
// as centenas de `sx={{ ... C.algo }}` espalhadas pelo arquivo continuam
// idênticas, e trocar de tema é trocar um atributo no <html> — sem
// re-render, sem context, sem prop passando por 2900 linhas.
//
// Consequência pra quem for mexer: `C.green` NÃO é mais uma string de cor
// utilizável em cálculo (nada de concatenar alpha, comparar com hex ou
// passar pra lib de canvas). É uma referência que só o CSS resolve.
const C = {
  bg:        'var(--ch-bg)',
  card:      'var(--ch-card)',
  surf:      'var(--ch-surf)',
  inset:     'var(--ch-inset)',
  bdr:       'var(--ch-bdr)',
  bdr2:      'var(--ch-bdr2)',
  green:     'var(--ch-green)',
  greenD:    'var(--ch-green-d)',
  greenBg:   'var(--ch-green-bg)',
  greenB:    'var(--ch-green-b)',
  greenGlow: 'var(--ch-green-glow)',
  amber:     'var(--ch-amber)',
  amberD:    'var(--ch-amber-d)',
  amberBg:   'var(--ch-amber-bg)',
  amberB:    'var(--ch-amber-b)',
  red:       'var(--ch-red)',
  redD:      'var(--ch-red-d)',
  redBg:     'var(--ch-red-bg)',
  redB:      'var(--ch-red-b)',
  blue:      'var(--ch-blue)',
  blueD:     'var(--ch-blue-d)',
  blueBg:    'var(--ch-blue-bg)',
  blueB:     'var(--ch-blue-b)',
  purple:    'var(--ch-purple)',
  purpD:     'var(--ch-purple-d)',
  purpBg:    'var(--ch-purple-bg)',
  purpB:     'var(--ch-purple-b)',
  pink:      'var(--ch-pink)',
  pinkD:     'var(--ch-pink-d)',
  pinkBg:    'var(--ch-pink-bg)',
  pinkB:     'var(--ch-pink-b)',
  text:      'var(--ch-text)',
  text2:     'var(--ch-text2)',
  text3:     'var(--ch-text3)',
  // Redesign de out/26 (ChatPage.css explica cada um).
  cap:       'var(--ch-cap)',
  ob:        'var(--ch-ob)',
  obB:       'var(--ch-ob-b)',
  obT:       'var(--ch-ob-t)',
  obM:       'var(--ch-ob-m)',
  glass:     'var(--ch-glass)',
  sh1:       'var(--ch-sh1)',
  sh2:       'var(--ch-sh2)',
  sel:       'var(--ch-sel)',
  hover:     'var(--ch-hover)',
  veu:       'var(--ch-veu)',
  sobreVerde: 'var(--ch-sobre-verde)',
};
// Fontes (out/26, redesign): Syne só nos nomes e títulos — chip, contato,
// topo da conversa, mini-página —, Outfit em todo o resto. Antes TUDO era
// Syne, inclusive o texto das mensagens e a caixa de digitar, e a Syne é
// larga e de desenho marcado: boa pra nome, cansativa pra ler parágrafo.
const FONT_TIT = "'Syne', sans-serif";
const FONT_TXT = "'Outfit', sans-serif";
const FONT_NUM = "'Outfit', sans-serif";
// O MUI pinta `Typography` sem `fontFamily` com a fonte do tema (Roboto), e
// o texto das bolhas e dos modelos caía nela. Só a tipografia muda; a paleta
// é a padrão, como antes.
const TEMA_FONTES = createTheme({ typography: { fontFamily: "'Outfit', system-ui, sans-serif" } });

// Estrela do filtro "Novos". Substituiu o emoji ✨ (set/26): emoji é
// renderizado pela fonte do sistema, então mudava de desenho e de peso
// entre Windows, macOS e Android, e não acompanhava a cor do chip quando
// ele ficava ativo. SVG com `currentColor` resolve os dois.
const IconNew = ({ size = 11 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true" style={{ flexShrink: 0 }}
  >
    <path d="m12 3 2.3 5.9 6.2.4-4.8 4 1.6 6-5.3-3.4L6.7 19l1.6-6-4.8-4 6.2-.4L12 3Z" />
  </svg>
);

// Gera uma cor de avatar consistente pelo nome (mesma da listagem).
function avatarPalette(name) {
  // Também por variável: os pastéis do claro brilhariam feito lâmpada no
  // escuro. O índice escolhido pelo hash do nome continua o mesmo, então o
  // avatar de um contato não troca de cor ao trocar de tema.
  const palettes = [
    { bg: 'var(--ch-av1-bg)', color: 'var(--ch-av1-fg)' },
    { bg: 'var(--ch-av2-bg)', color: 'var(--ch-av2-fg)' },
    { bg: 'var(--ch-av3-bg)', color: 'var(--ch-av3-fg)' },
    { bg: 'var(--ch-av4-bg)', color: 'var(--ch-av4-fg)' },
    { bg: 'var(--ch-av5-bg)', color: 'var(--ch-av5-fg)' },
    { bg: 'var(--ch-av6-bg)', color: 'var(--ch-av6-fg)' },
    { bg: 'var(--ch-av7-bg)', color: 'var(--ch-av7-fg)' },
    { bg: 'var(--ch-av8-bg)', color: 'var(--ch-av8-fg)' },
  ];
  const idx = (name || '?').charCodeAt(0) % palettes.length;
  return palettes[idx];
}

// Health color (mesma regra do Conversations.js)
function healthColor(score) {
  if (score >= 80) return C.green;
  if (score >= 50) return C.amber;
  return C.red;
}

export default function ChatPage() {
  const navigate = useNavigate();
  const { deviceId, clientId } = useParams();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const theme = useTheme();
  // Layout WhatsApp-mobile: em telas <900px alterna entre "lista" e "chat"
  // no lugar das 3 colunas do desktop. Panel de templates (bulk) fica
  // escondido em mobile — a decisão foi fluidez do 1-a-1.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ─── Estado principal ───────────────────────────────────────────────────
  const [device, setDevice] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  // ─── Emoji e reação (out/26) ──────────────────────────────────────────
  // Dois pontos de abertura do mesmo painel, com âncoras diferentes:
  // o botão do rodapé (escrever) e a bolha (reagir). `reacaoAlvo` guarda
  // a mensagem junto da âncora porque o painel precisa saber em QUAL
  // bolha a reação vai, e a bolha já saiu de cena quando ele fecha.
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [reacaoAlvo, setReacaoAlvo] = useState(null);
  const [loadingClients, setLoadingClients] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  // Contador da gravação. Existe pra o agente SABER quanto já falou antes
  // de soltar o botão — era gravar no escuro e descobrir o tamanho só no
  // erro de envio.
  const [segundosGravando, setSegundosGravando] = useState(0);
  const [searchClients, setSearchClients] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  // Filtro atual do painel esquerdo. UM por vez — escolher qualquer um
  // substitui o anterior. Formatos suportados:
  //   'all'                          → todas as conversas
  //   'unread'                       → só com unread > 0
  //   'new'                          → só os "Novos" (regra derivada,
  //                                    ver `isNew` em enrichedClients)
  //   { type: 'label', id, name }    → só clientes com essa etiqueta
  //   { type: 'folder', id, name }   → só clientes nessa pasta do CRM
  //   { type: 'interest', value }    → só clientes com esse status CRM
  //
  // Pasta e interesse são EIXOS DIFERENTES e um contato tem os dois:
  // pasta é onde ele foi guardado, interesse é o quanto está quente. Até
  // out/26 o chip "Pastas" filtrava por interesse — se for mexer aqui,
  // não junte os dois de novo.
  const [filterTab, setFilterTab] = useState('all');
  // Anchor pros três dropdowns (Etiquetas / Pastas / Interesse). Menu MUI
  // abre logo abaixo do chip clicado — mesmo padrão do label-picker do CRM.
  const [labelMenuAnchor, setLabelMenuAnchor] = useState(null);
  const [folderMenuAnchor, setFolderMenuAnchor] = useState(null);
  const [interestMenuAnchor, setInterestMenuAnchor] = useState(null);

  // Paginação client-side da LISTA DE CONVERSAS.
  // Motivo: chip com 358+ conversas rendia 358 items no DOM (avatar +
  // preview + labels + timestamps). O gargalo era render, não rede — o
  // payload do /conversations/list é pequeno mesmo com muitos clientes.
  // Client-side pagination resolve o render sem quebrar filtros (que
  // dependem da lista completa em memória) nem o polling/WS.
  //
  // Comportamento: mostra 50 iniciais. Cada clique em "Ver mais" soma
  // +20. Reset pra 50 quando o filtro ou a busca muda (novo escopo).
  const CONVERSATIONS_PAGE_SIZE = 50;
  const CONVERSATIONS_PAGE_STEP = 20;
  // Espelham os defaults do backend (routers/conversations). Só valem até
  // o primeiro fetch — depois o header manda, porque mudar o número lá não
  // pode exigir deploy do frontend.
  const CONVERSATIONS_SERVER_LIMIT = 500;
  const CONVERSATIONS_WARN_AT = 1700;
  const [conversationsVisible, setConversationsVisible] = useState(CONVERSATIONS_PAGE_SIZE);

  // Quantas conversas existem no chip (vem no header), quantas o servidor
  // mandou, e a partir de quantas a Cetto recomenda limpar. `null` enquanto
  // não carregou — zero significaria "nenhuma conversa" e acenderia o
  // estado vazio na cara de quem só está esperando a rede.
  const [totalConversas, setTotalConversas] = useState(null);
  const [limiteCarga, setLimiteCarga] = useState(CONVERSATIONS_SERVER_LIMIT);
  const [avisoConversas, setAvisoConversas] = useState(CONVERSATIONS_WARN_AT);
  const [limpezaAberta, setLimpezaAberta] = useState(false);

  // Mini-página do contato (out/26, redesign). Substituiu o popover "i",
  // que só mostrava as anotações. Aberta ou fechada fica guardado: quem
  // gosta dela aberta não precisa reabrir a cada conversa. No celular é uma
  // folha e sempre começa fechada.
  const [contatoAberto, setContatoAberto] = useState(() => {
    try { return localStorage.getItem('cetto:chat-contato') === 'aberto'; } catch { return false; }
  });
  const alternarContato = useCallback((abrir) => {
    setContatoAberto((atual) => {
      const novo = typeof abrir === 'boolean' ? abrir : !atual;
      try { localStorage.setItem('cetto:chat-contato', novo ? 'aberto' : 'fechado'); } catch { /* storage indisponível */ }
      return novo;
    });
  }, []);
  const [contatoFolha, setContatoFolha] = useState(false);
  // A folha do contato (celular) é da conversa aberta: trocou, fecha.
  useEffect(() => { setContatoFolha(false); }, [selectedClient?.id]);

  // ─── Responder e encaminhar (nov/26) ──────────────────────────────────
  // `respondendo` = a mensagem que o próximo envio vai citar. Vale pra
  // texto, mídia, áudio e arquivo da IA — o ref existe porque o envio do
  // áudio roda no `onstop` do gravador, que enxerga o estado de quando a
  // gravação COMEÇOU. Trocar de conversa solta a citação (citar uma
  // mensagem de outro contato seria 404 no servidor).
  const [respondendo, setRespondendoEstado] = useState(null);
  const respondendoRef = useRef(null);
  const setRespondendo = useCallback((msg) => {
    respondendoRef.current = msg;
    setRespondendoEstado(msg);
  }, []);
  useEffect(() => { setRespondendo(null); }, [selectedClient?.id, setRespondendo]);
  // A citação do próximo envio — e solta, pra não ir em dois envios.
  const tirarCitacao = () => {
    const citada = respondendoRef.current;
    if (citada) setRespondendo(null);
    return citada;
  };
  const [encaminhando, setEncaminhando] = useState(null);   // mensagem
  const [acoesMsg, setAcoesMsg] = useState(null);           // { msg, anchorEl } (celular)
  const toqueBolhaRef = useRef({ timer: null });
  const [destacada, setDestacada] = useState(null);         // id piscando ao ir pra citada
  const [aviso, setAviso] = useState('');
  // O "+ etiqueta" abre o MESMO popover em três lugares (linha da lista,
  // topo e mini-página); este diz se ele aparece na mini-página.
  const [etiquetaNoPainel, setEtiquetaNoPainel] = useState(false);
  // Celular: ferramentas da caixa de mensagem no "+", e a seleção de
  // conversas por toque longo (sem o círculo sempre à vista).
  const [menuMaisAnchor, setMenuMaisAnchor] = useState(null);
  const botaoMaisRef = useRef(null);
  const toqueLongoRef = useRef({ timer: null, disparou: false });

  // ─── IA Assistida (sugestão de resposta sob demanda) ────────────────────
  // O botão de IA só existe se a company conectou uma chave E deixou a IA
  // ligada (Automação → IA Assistida). Config é buscada uma vez no mount e
  // fica em cache local — não muda no meio de um atendimento.
  //
  // Nada aqui gera sugestão sozinho: a chamada só sai no clique do agente.
  // É o princípio do produto (humano aprova) e também controle de custo.
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiCardOpen, setAiCardOpen] = useState(false);
  const [aiAnchorRect, setAiAnchorRect] = useState(null);
  // Snapshot do input no momento em que o card abriu. Se o agente digitar
  // qualquer coisa depois disso, a sugestão perdeu validade e o card fecha
  // (o próprio card registra DISCARDED).
  const aiInputSnapshotRef = useRef('');

  // Qualificação do lead (out/26, rastreamento PR 2): marcado pelo topo do
  // chat, a lista e a conversa aberta mudam juntas (o `lead_status` vem do
  // ClientOut; o detalhe — motivo, valor — fica no CRM).
  const aoQualificarChat = useCallback((id, nova) => {
    const st = nova?.status || null;
    setClients(prev => prev.map(c => (c.id === id ? { ...c, lead_status: st } : c)));
    setSelectedClient(prev => (prev && prev.id === id ? { ...prev, lead_status: st } : prev));
  }, []);

  // Multi-select + bulk template
  const [selectedConvIds, setSelectedConvIds] = useState(new Set());
  const [showTmplPanel, setShowTmplPanel] = useState(false);
  // Formulários PR 2 (out/26): diálogo "Enviar formulário".
  const [formOpen, setFormOpen] = useState(false);
  const [pickedTemplateName, setPickedTemplateName] = useState(null);
  // Variáveis pendentes de um template prestes a ser enviado (out/26).
  const [varsPrompt, setVarsPrompt] = useState(null);
  const [sendingVars, setSendingVars] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { sent, failed }
  // Blindagem contra banimento (out/26): o preflight do lote (tema, conta
  // na Meta, teto do teste, quem nunca conversou) e a caixa de opt-in.
  const [bulkPreflight, setBulkPreflight] = useState(null);
  const [bulkOptin, setBulkOptin] = useState(false);

  // ─── Etiquetas (labels) ─────────────────────────────────────────────────
  // - `allLabels`: catálogo da company (vem de GET /labels)
  // - `labelPopoverClientId`: qual conv está com o popover aberto (null = nenhum)
  // - `labelSearch`: busca dentro do popover
  // - `creatingLabel`: estado do fluxo "criar nova etiqueta" inline
  const [allLabels, setAllLabels] = useState([]);
  const [labelPopoverClientId, setLabelPopoverClientId] = useState(null);
  const [labelSearch, setLabelSearch] = useState('');
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [newLabelColor, setNewLabelColor] = useState('green');
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // Nova conversa
  const [openNewConvModal, setOpenNewConvModal] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', phone: '' });
  const [newConvLoading, setNewConvLoading] = useState(false);
  const [newConvError, setNewConvError] = useState('');

  // ─── Unread persistido por device (igual a v2) ──────────────────────────
  const UNREAD_LS_KEY = useMemo(() => `cetto.chat.unread.${deviceId || 'noop'}`, [deviceId]);
  const [unreadByClient, setUnreadByClient] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cetto.chat.unread.${deviceId || 'noop'}`)) || {}; }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(UNREAD_LS_KEY, JSON.stringify(unreadByClient)); }
    catch { /* quota cheia: ignora */ }
  }, [unreadByClient, UNREAD_LS_KEY]);

  const bumpUnread = useCallback((id) => {
    setUnreadByClient(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  }, []);
  const resetUnread = useCallback((id) => {
    setUnreadByClient(prev => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // ─── Refs (anti-closure-stale + scroll inteligente) ─────────────────────
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const isNearBottomRef = useRef(true);
  const clientsRef = useRef(null);
  const seenInboundIdsRef = useRef(new Set());
  const prevMessagesLengthRef = useRef(0);
  const prevSelectedClientIdRef = useRef(null);

  const selectedClientRef = useRef(null);
  const fetchClientsRef = useRef(null);
  const fetchMessagesRef = useRef(null);
  // Ref pro <textarea> do composer. Usado pelo auto-grow (set/26 UX):
  // recalcula altura conforme o usuário digita, cresce até 10 linhas
  // e depois vira scroll interno — mesmo comportamento do WhatsApp
  // e do compositor do Slack. Sem isso o texto grande virava uma linha
  // horizontal quase ilegível antes do envio.
  const inputRef = useRef(null);
  useEffect(() => { selectedClientRef.current = selectedClient; }, [selectedClient]);

  const token = localStorage.getItem('token');

  // ─── Carrega device (header chip + saúde) ───────────────────────────────
  useEffect(() => {
    if (!deviceId || !token) return;
    api.get(`/devices/${deviceId}`).then(r => setDevice(r.data)).catch(() => setDevice(null));
  }, [deviceId, token]);

  // ─── Etiquetas (fetch + helpers) ────────────────────────────────────────
  const fetchLabels = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await api.get('/labels');
      setAllLabels(r.data || []);
    } catch {
      setAllLabels([]);
    }
  }, [deviceId]);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  // ─── Pastas (catálogo da company) ───────────────────────────────────────
  // Alimenta o chip "Pastas". Até out/26 aquele chip listava os NÍVEIS DE
  // INTERESSE do CRM — pasta de verdade não aparecia ali, e o agente que
  // criava "Academia" no CRM não achava o filtro no chat.
  const [allFolders, setAllFolders] = useState([]);

  const fetchFolders = useCallback(async () => {
    if (!deviceId) return;
    try {
      const r = await api.get('/folders');
      setAllFolders(Array.isArray(r.data) ? r.data : (r.data?.folders || []));
    } catch {
      setAllFolders([]);
    }
  }, [deviceId]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  // Recarrega o catálogo quando a aba volta ao foco. Pasta é criada e
  // apagada na tela de Contatos, quase sempre em OUTRA aba — sem isto, o
  // dropdown do chat só via o catálogo do momento em que a página abriu.
  useEffect(() => {
    const aoVoltar = () => { if (!document.hidden) fetchFolders(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    return () => {
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
    };
  }, [fetchFolders]);

  // Pasta apagada com o filtro ativo prendia o agente numa lista VAZIA:
  // nenhuma conversa casa com o id morto, o chip seguia exibindo o nome
  // antigo e não havia nada indicando o motivo. Cai pra "Todas".
  //
  // Só age depois que o catálogo carregou de fato (`length > 0`), senão
  // uma falha momentânea de rede limparia o filtro de quem não pediu.
  useEffect(() => {
    if (typeof filterTab !== 'object' || filterTab?.type !== 'folder') return;
    if (allFolders.length === 0) return;
    if (!allFolders.some(f => f.id === filterTab.id)) setFilterTab('all');
  }, [allFolders, filterTab]);

  // Toggle: attach se não tem, detach se tem. Update otimista — em caso
  // de erro, refetch da lista pra voltar ao state do servidor.
  //
  // IMPORTANTE: NÃO listamos `fetchClients` nas deps porque ele é
  // declarado MAIS PRA BAIXO neste mesmo componente. Em produção o
  // bundler minifica e a ordem importa: `Cannot access 'fetchClients'
  // before initialization` se virar TDZ. Usamos fetchClientsRef.current
  // (declarado lá em cima como `useRef(null)` e populado por useEffect
  // depois que fetchClients existe).
  const toggleLabelOnClient = useCallback(async (clientId, labelId) => {
    const target = clients.find(c => c.id === clientId);
    if (!target) return;
    const hasIt = (target.labels || []).some(l => l.id === labelId);
    const labelObj = allLabels.find(l => l.id === labelId);

    setClients(prev => prev.map(c => {
      if (c.id !== clientId) return c;
      const labels = c.labels || [];
      if (hasIt) {
        return { ...c, labels: labels.filter(l => l.id !== labelId) };
      } else if (labelObj) {
        return { ...c, labels: [...labels, labelObj] };
      }
      return c;
    }));

    try {
      if (hasIt) {
        await api.delete(`/labels/${labelId}/clients/${clientId}`);
      } else {
        await api.post(`/labels/${labelId}/clients/${clientId}`);
      }
    } catch (err) {
      // Rollback via refetch — usa ref pra não criar dependência circular
      if (fetchClientsRef.current) fetchClientsRef.current();
      alert(err.response?.data?.detail || 'Erro ao atualizar etiqueta');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, allLabels]);

  // Deleta etiqueta da company inteira (soft-delete no backend). Some
  // dos clientes que a tinham + do catálogo do popover. Confirmação
  // leve com window.confirm — é ação destrutiva mas reversível
  // (o backend só marca deleted_at, dá pra restaurar via SQL).
  const deleteLabel = useCallback(async (labelId, labelName) => {
    const ok = window.confirm(
      `Excluir a etiqueta "${labelName}"?\n\nEla será removida de TODOS os contatos da empresa que a tenham.`
    );
    if (!ok) return;
    // Optimistic: tira do catálogo e das rows.
    setAllLabels(prev => prev.filter(l => l.id !== labelId));
    setClients(prev => prev.map(c => ({
      ...c,
      labels: (c.labels || []).filter(l => l.id !== labelId),
    })));
    try {
      await api.delete(`/labels/${labelId}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir etiqueta');
      // Refaz o catálogo e a lista de conversas em caso de falha.
      fetchLabels();
      if (fetchClientsRef.current) fetchClientsRef.current();
    }
  }, [fetchLabels]);

  const createNewLabel = useCallback(async (name, color, attachToClientId = null) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    try {
      const r = await api.post('/labels', { name: trimmed, color });
      const created = r.data;
      setAllLabels(prev => [...prev, created]);
      // Se foi criada inline pra um cliente específico, já atribui
      if (attachToClientId) {
        await api.post(`/labels/${created.id}/clients/${attachToClientId}`);
        setClients(prev => prev.map(c =>
          c.id === attachToClientId
            ? { ...c, labels: [...(c.labels || []), created] }
            : c
        ));
      }
      return created;
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao criar etiqueta');
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Templates dinâmicos (Meta) ─────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    if (!deviceId) return;
    setLoadingTemplates(true);
    setTemplatesError(null);
    try {
      const res = await api.get(`/messages/templates/${deviceId}`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Não foi possível carregar os templates.';
      setTemplatesError(detail);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [deviceId]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ─── Scroll inteligente ─────────────────────────────────────────────────
  const scrollToBottom = (smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    }
  };
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 120;
  }, []);
  useEffect(() => {
    const curLen = messages.length;
    const prevLen = prevMessagesLengthRef.current;
    const curSelId = selectedClient?.id || null;
    const prevSelId = prevSelectedClientIdRef.current;
    const switchedClient = curSelId !== prevSelId;
    const grew = curLen > prevLen;
    if (switchedClient) {
      scrollToBottom(false);
      isNearBottomRef.current = true;
    } else if (grew && isNearBottomRef.current) {
      scrollToBottom(prevLen > 0);
    }
    prevMessagesLengthRef.current = curLen;
    prevSelectedClientIdRef.current = curSelId;
  }, [messages, selectedClient?.id]);

  // ─── Lista de conversas (com detecção de inbound nova) ──────────────────
  // include_unmessaged: só ativa quando o usuário selecionou o filtro
  // "Novos" — nesse caso o backend traz também contatos criados que
  // NUNCA tiveram msg (conversas apagadas continuam invisíveis).
  // Adicionar `includeUnmessaged` na dep do useCallback garante refetch
  // automático quando o filtro entra/sai de 'new'; outras mudanças de
  // filterTab (label, interest, unread) não recriam o fetch — filtros
  // adicionais rodam client-side sem round-trip.
  const includeUnmessaged = filterTab === 'new';
  const fetchClients = useCallback(async () => {
    if (!deviceId) return;
    try {
      const res = await api.get(
        `/conversations/device/${deviceId}/list`,
        { params: includeUnmessaged ? { include_unmessaged: 1 } : {} },
      );
      // O servidor passou a mandar só as mais recentes (out/26) e a dizer
      // no header quantas existem no total. É com esse número que o chat
      // sabe que há conversa fora da lista e oferece a limpeza — antes ele
      // baixava as 1.641 da Pontal a cada 10 segundos de polling.
      const lido = (chave, padrao) => {
        const v = parseInt(res.headers?.[chave.toLowerCase()], 10);
        return Number.isFinite(v) ? v : padrao;
      };
      setTotalConversas(lido('X-Total-Conversas', null));
      setLimiteCarga(lido('X-Limite-Conversas', 500));
      setAvisoConversas(lido('X-Aviso-Conversas', 1700));
      // Sort: pinned primeiro (na ordem de fixação = pinned_at ASC),
      // resto por last_message.timestamp DESC. O backend já devolve nessa
      // ordem, mas re-sortamos aqui pra garantir consistência quando o
      // frontend faz optimistic updates (ex.: togglePin altera is_pinned
      // localmente antes do refetch).
      const sorted = (res.data || []).slice().sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        if (a.is_pinned && b.is_pinned) {
          const aPin = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
          const bPin = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
          return aPin - bPin;
        }
        const aTs = a?.last_message?.timestamp ? new Date(a.last_message.timestamp).getTime() : 0;
        const bTs = b?.last_message?.timestamp ? new Date(b.last_message.timestamp).getTime() : 0;
        return bTs - aTs;
      });

      const isFirstLoad = !clientsRef.current;
      const currentSelectedId = selectedClientRef.current?.id;
      sorted.forEach(c => {
        const lm = c?.last_message;
        if (!lm) return;
        const dir = (lm.direction || '').toUpperCase();
        const msgKey = lm.whatsapp_message_id || lm.id;
        if (dir !== 'INBOUND' || !msgKey) return;
        if (seenInboundIdsRef.current.has(msgKey)) return;
        seenInboundIdsRef.current.add(msgKey);
        if (isFirstLoad) return;
        if (c.id === currentSelectedId) return;
        bumpUnread(c.id);
      });

      setClients(sorted);
      clientsRef.current = sorted;
      setLoadingClients(false);
    } catch (err) {
      try {
        const fallback = await api.get(`/clients/device/${deviceId}`);
        const sortedFb = fallback.data.sort((a, b) => new Date(b.last_interaction) - new Date(a.last_interaction));
        setClients(sortedFb);
        clientsRef.current = sortedFb;
      } catch { /* ignora */ }
      finally { setLoadingClients(false); }
    }
  }, [deviceId, bumpUnread, includeUnmessaged]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { fetchClientsRef.current = fetchClients; }, [fetchClients]);

  // Auto-selecionar cliente via URL
  useEffect(() => {
    if (!clientId) return;
    if (selectedClient?.id === clientId) return;
    if (clients.length === 0) return;
    const found = clients.find(c => c.id === clientId);
    if (found) {
      setSelectedClient(found);
      resetUnread(clientId);
    } else {
      api.get(`/clients/${clientId}`)
        .then(r => {
          setSelectedClient(r.data);
          setClients(prev => prev.find(c => c.id === clientId) ? prev : [r.data, ...prev]);
        })
        .catch(() => alert('Cliente não encontrado.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clients.length]);

  const handleSelectClient = useCallback((client) => {
    if (!client) return;
    setSelectedClient(client);
    setMessages([]);
    resetUnread(client.id);
    if (deviceId && client.id) {
      navigate(`/chat/${deviceId}/${client.id}`, { replace: true });
    }
  }, [deviceId, navigate, resetUnread]);

  // ─── Mensagens (paginadas) ──────────────────────────────────────────────
  // Estratégia: carrega as 50 mais recentes na abertura. Ao rolar até o
  // topo do chat, aparece um botão "Ver mais 20" que faz load-more por
  // cursor (before_id = id da msg mais antiga já em tela).
  //
  // has_more vem do backend (query pega limit+1 e testa se sobra).
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  const fetchMessages = useCallback(async () => {
    if (!selectedClient || !deviceId) return;
    try {
      const res = await api.get(
        `/conversations/device/${deviceId}/client/${selectedClient.id}`,
        { params: { limit: 50 } },
      );
      // Backend já devolve em ordem cronológica ASC.
      const items = res.data?.messages || [];
      setMessagesHasMore(!!res.data?.has_more);
      setMessages(prev => {
        // Atalho pra não re-renderizar a conversa inteira a cada polling
        // (10s) quando nada mudou.
        //
        // Out/26: ele comparava só a ÚLTIMA mensagem — então qualquer
        // mudança numa linha anterior passava batida. Era um bug latente
        // em status de entrega (o ✓✓ de uma mensagem do meio nunca
        // aparecia sem trocar de conversa) e viraria bug visível com
        // reação, que por definição acontece numa mensagem que já está na
        // tela. Agora a assinatura cobre todas as linhas e os campos que
        // mudam depois de gravadas.
        if (prev.length !== items.length || prev.length === 0) return items;
        const assinar = (lista) => lista.map(
          m => `${m.id}|${m.status || ''}|${m.reaction_in || ''}|${m.reaction_out || ''}`,
        ).join('~');
        return assinar(prev) === assinar(items) ? prev : items;
      });
    } catch {
      setMessages([]);
      setMessagesHasMore(false);
    }
  }, [selectedClient, deviceId]);

  const loadOlderMessages = useCallback(async () => {
    if (!selectedClient || !deviceId || !messagesHasMore || loadingOlderMessages) return;
    if (messages.length === 0) return;
    const first = messages[0];
    if (!first?.id) return;
    setLoadingOlderMessages(true);
    // Preserva scroll: guardamos a altura antes do prepend e ajustamos
    // scrollTop depois pra que a mesma mensagem continue no mesmo lugar
    // (evita "pular" pro topo quando novas antigas chegam).
    const el = messagesContainerRef.current;
    const prevHeight = el?.scrollHeight || 0;
    try {
      const res = await api.get(
        `/conversations/device/${deviceId}/client/${selectedClient.id}`,
        { params: { limit: 20, before_id: first.id } },
      );
      const older = res.data?.messages || [];
      setMessagesHasMore(!!res.data?.has_more);
      if (older.length > 0) {
        setMessages(prev => {
          // Dedup defensivo — evita mostrar msg em duplicata se dois requests
          // colidirem (raro, mas simples de proteger).
          const seen = new Set(prev.map(m => m.id).filter(Boolean));
          const merged = older.filter(m => !seen.has(m.id));
          return [...merged, ...prev];
        });
        // Restaura a posição relativa após o React re-renderizar.
        setTimeout(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        }, 0);
      }
    } catch {
      // silenciosa — se falhou, o botão fica visível pra tentar de novo
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [selectedClient, deviceId, messagesHasMore, loadingOlderMessages, messages]);

  useEffect(() => { if (selectedClient) fetchMessages(); }, [selectedClient, fetchMessages]);
  useEffect(() => { fetchMessagesRef.current = fetchMessages; }, [fetchMessages]);

  // ─── WebSocket + Polling fallback + visibility/online handlers ──────────
  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const connectWebSocket = useCallback(() => {
    if (!deviceId || !token) return;
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      try { wsRef.current.close(1000, 'Reconnecting'); } catch {}
    }
    stopHeartbeat();

    const wsBaseUrl = (process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br')
      .replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsBaseUrl}/ws/${deviceId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      // Autenticação: o token vai no PRIMEIRO frame, antes de tudo.
      //
      // Out/26: o canal não tinha autenticação — qualquer um com o UUID
      // do chip (que está na própria URL desta página) recebia as
      // mensagens. Agora o servidor só registra o socket pra broadcast
      // depois de validar este frame; sem ele, fecha em 10s.
      //
      // No primeiro frame, e não na URL (`?token=`), porque o Nginx grava
      // a URL no access log e o JWT ficaria em disco em texto puro.
      //
      // Lido do localStorage AGORA, não da variável capturada no render:
      // numa reconexão depois de um novo login, o token antigo seria
      // recusado.
      try {
        ws.send(JSON.stringify({ type: 'auth', token: localStorage.getItem('token') || '' }));
      } catch {}
      setWsConnected(true);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (fetchClientsRef.current) fetchClientsRef.current();
      if (fetchMessagesRef.current) fetchMessagesRef.current();
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ type: 'ping', ts: Date.now() })); }
          catch { try { ws.close(); } catch {} }
        }
      }, 25000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong' || data.type === 'ping') return;
        // ─── Reação ──────────────────────────────────────────────────
        // Evento próprio, não `new_message`: a reação não cria mensagem
        // nenhuma, ela muda uma que já está na lista. `emoji: ""` é
        // remoção — o contato tirou a reação dele.
        //
        // Não bumpa badge de não-lida: reação não é mensagem pra
        // responder, e marcar a conversa como nova por um 👍 mandaria o
        // agente abrir uma conversa onde não há nada novo pra ler.
        if (data.type === 'reaction') {
          if (fetchClientsRef.current) fetchClientsRef.current();
          const atual = selectedClientRef.current;
          if (!atual || data.client_id !== atual.id) return;
          const campo = data.lado === 'out' ? 'reaction_out' : 'reaction_in';
          setMessages(prev => prev.map(m => (
            m.id === data.message_id
              ? { ...m, [campo]: data.emoji || null }
              : m
          )));
          return;
        }
        if (data.type === 'new_message') {
          if (fetchClientsRef.current) fetchClientsRef.current();
          const currentClient = selectedClientRef.current;
          const newMsg = data.message;
          const isForCurrent = currentClient && data.client_id === currentClient.id;
          if (isForCurrent) {
            setMessages(prev => {
              if (prev.find(m => m.id === newMsg.id || m.whatsapp_message_id === newMsg.whatsapp_message_id)) return prev;
              return [...prev, {
                id: newMsg.id, whatsapp_message_id: newMsg.whatsapp_message_id,
                content: newMsg.content, direction: newMsg.direction,
                message_type: newMsg.type, media_url: newMsg.media_url,
                timestamp: newMsg.timestamp, status: 'DELIVERED',
                // Resposta de formulário chega desenhada daqui (out/26).
                template_render: newMsg.template_render || null,
                // Responder e encaminhar (nov/26).
                citacao: newMsg.citacao || null,
                forwarded: newMsg.forwarded || null,
                contatos: newMsg.contatos || null,
              }];
            });
            const msgKey = newMsg.whatsapp_message_id || newMsg.id;
            if (msgKey) seenInboundIdsRef.current.add(msgKey);
          } else if ((newMsg?.direction || '').toUpperCase() === 'INBOUND') {
            const msgKey = newMsg.whatsapp_message_id || newMsg.id;
            if (msgKey && !seenInboundIdsRef.current.has(msgKey)) {
              seenInboundIdsRef.current.add(msgKey);
              bumpUnread(data.client_id);
            } else if (!msgKey) {
              bumpUnread(data.client_id);
            }
          }
        }
      } catch {}
    };

    ws.onclose = (event) => {
      setWsConnected(false);
      stopHeartbeat();
      // 4401 = token recusado; 4403 = chip de outra empresa ou apagado.
      // Reconectar com o mesmo token daria a mesma resposta — a cada 3s,
      // pra sempre. O polling de 10s segue entregando as mensagens, e se
      // o token expirou é a próxima chamada REST que manda pro login.
      //
      // 4408 (não autenticou a tempo) reconecta normalmente: é rede
      // lenta, não recusa.
      if (event.code === 4401 || event.code === 4403) return;
      if (event.code !== 1000) {
        reconnectTimeoutRef.current = setTimeout(() => connectWebSocket(), 3000);
      }
    };
    ws.onerror = () => setWsConnected(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, token]);

  useEffect(() => {
    connectWebSocket();
    pollIntervalRef.current = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (fetchClientsRef.current) fetchClientsRef.current();
      if (selectedClientRef.current && fetchMessagesRef.current) fetchMessagesRef.current();
    }, 10000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (fetchClientsRef.current) fetchClientsRef.current();
        if (fetchMessagesRef.current) fetchMessagesRef.current();
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) connectWebSocket();
      }
    };
    const handleOnline = () => connectWebSocket();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      stopHeartbeat();
      if (wsRef.current) {
        try { wsRef.current.close(1000, 'Component unmounted'); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  // ─── Envio de mensagem ──────────────────────────────────────────────────
  // `overrideText` existe pra o fluxo "Usar e enviar" da sugestão de IA:
  // setInputText é assíncrono, então o card passa o texto direto em vez de
  // depender do state ter atualizado. Os call sites normais (onClick, Enter)
  // chamam sem argumento string e continuam lendo `inputText`.
  const handleSendMessage = async (overrideText) => {
    const raw = typeof overrideText === 'string' ? overrideText : inputText;
    if (!raw.trim() || !selectedClient) return;
    const textToSend = raw;
    setInputText('');
    setIsTyping(true);
    const citada = tirarCitacao();
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      content: textToSend,
      direction: 'OUTBOUND',
      message_type: 'TEXT',
      timestamp: new Date().toISOString(),
      status: 'SENT',
      // A bolha otimista já nasce com a citação; a do servidor chega igual.
      citacao: citada ? citacaoLocal(citada, urlDaMidia) : null,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    try {
      await api.post('/messages/send', {
        device_id: deviceId,
        to: selectedClient.phone_number,
        body: textToSend,
        ...(citada ? { reply_to: citada.id } : {}),
      });
      setTimeout(() => fetchMessages(), 1000);
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id));
      // O texto volta pra caixa: quem digitou um parágrafo não perde tudo
      // porque a citação deu 404/409.
      setInputText(textToSend);
      if (citada) setRespondendo(citada);
      const detalhe = err?.response?.data?.detail;
      alert(typeof detalhe === 'string' ? detalhe : 'Erro ao enviar mensagem.');
    } finally {
      setIsTyping(false);
    }
  };

  // ─── Emoji no campo de mensagem ─────────────────────────────────────────
  // Insere no CARET, não no fim: quem já escreveu "obrigado pela paciência"
  // e quer o emoji depois de "obrigado" não deveria ter que reescrever a
  // frase. `setRangeText` faz isso sem mexer no resto do texto; o fallback
  // cobre o caso de o textarea não estar montado.
  const inserirEmoji = (emoji) => {
    if (!emoji) return;
    const campo = inputRef.current;
    if (!campo) {
      setInputText(prev => prev + emoji);
      return;
    }
    const inicio = campo.selectionStart ?? campo.value.length;
    const fim = campo.selectionEnd ?? campo.value.length;
    const novo = campo.value.slice(0, inicio) + emoji + campo.value.slice(fim);
    setInputText(novo);
    // Devolve o foco e põe o cursor DEPOIS do emoji, pra a pessoa seguir
    // digitando. No próximo frame: antes do React pintar o valor novo, o
    // cursor voltaria pra posição antiga.
    requestAnimationFrame(() => {
      campo.focus();
      const pos = inicio + emoji.length;
      try { campo.setSelectionRange(pos, pos); } catch {}
    });
  };

  // ─── Reagir a uma mensagem ──────────────────────────────────────────────
  // Otimista com rollback: a reação é um gesto, e esperar o ida-e-volta da
  // Graph API (até alguns segundos) faria o clique parecer ignorado. Se a
  // Meta recusar, o selo volta ao que era e o motivo dela aparece — o
  // backend devolve 409 quando a janela de 24h fechou, que é o caso mais
  // provável de recusa e precisa ser lido, não engolido.
  const reagir = async (msg, emoji) => {
    if (!msg?.id) return;
    setReacaoAlvo(null);
    const anterior = msg.reaction_out || null;
    const aplicado = emoji || null;
    if (anterior === aplicado) return;

    const patch = (valor) => setMessages(prev => prev.map(
      m => (m.id === msg.id ? { ...m, reaction_out: valor } : m),
    ));
    patch(aplicado);
    try {
      await api.post('/messages/reaction', { message_id: msg.id, emoji });
    } catch (err) {
      patch(anterior);
      alert(
        err?.response?.data?.detail
        || 'Não foi possível enviar a reação. Tente de novo.',
      );
    }
  };

  // A mensagem do painel de reação, relida do state a cada render em vez
  // de usar o snapshot guardado na abertura. Com o painel aberto a reação
  // pode mudar por fora (o contato reagiu, o dono reagiu pelo celular) — e
  // aí o destaque do emoji aplicado tem que acompanhar, senão clicar nele
  // tentaria aplicar o que já está lá.
  const msgEmReacao = reacaoAlvo
    ? (messages.find(m => m.id === reacaoAlvo.msg?.id) || reacaoAlvo.msg)
    : null;

  // ─── IA Assistida ───────────────────────────────────────────────────────
  // Config lida uma vez no mount. Falha silenciosa: se /ai/config não
  // responder (plano sem IA, backend antigo), o botão simplesmente não
  // aparece — nunca quebra o chat.
  useEffect(() => {
    let alive = true;
    api.get('/ai/config')
      .then(res => { if (alive) setAiEnabled(!!res.data?.enabled); })
      .catch(() => { if (alive) setAiEnabled(false); });
    return () => { alive = false; };
  }, []);

  // Trocar de conversa invalida a sugestão aberta.
  useEffect(() => { setAiCardOpen(false); }, [selectedClient?.id]);

  // Auto-invalidação: o agente começou a escrever a própria resposta, então
  // a sugestão não serve mais. Compara com o snapshot pra não fechar o card
  // só porque já havia rascunho no input quando ele clicou.
  useEffect(() => {
    if (aiCardOpen && inputText !== aiInputSnapshotRef.current) setAiCardOpen(false);
  }, [aiCardOpen, inputText]);

  // Toggle, não "abrir": clicar de novo com o card aberto fecha. Se o botão
  // só abrisse, o clique-fora fecharia e o clique reabriria — segunda
  // chamada paga ao provedor sem o agente ter pedido nada.
  const handleToggleAiCard = (e) => {
    if (aiCardOpen) { setAiCardOpen(false); return; }
    if (!selectedClient || messages.length === 0) return;
    aiInputSnapshotRef.current = inputText;
    setAiAnchorRect(e.currentTarget.getBoundingClientRect());
    setAiCardOpen(true);
  };

  // [Usar e enviar] — vai direto pro WhatsApp do cliente, sem passo extra.
  // [Usar] — manda a sugestão. Quando a IA pediu um arquivo da biblioteca
  // (out/26), o anexo vai NO LUGAR do texto solto: o texto é a legenda do
  // arquivo, e enviar os dois separados faria chegar duas mensagens, a
  // primeira sem sentido sozinha.
  const handleAiUseText = useCallback((text, arquivo) => {
    if (!arquivo) { handleSendMessage(text); return; }
    const destino = selectedClient?.phone_number;
    if (!destino) return;
    const citada = tirarCitacao();
    api.post('/messages/send-asset', {
      device_id: deviceId,
      to: destino,
      asset_id: arquivo.id,
      caption: text || '',
      ...(citada ? { reply_to: citada.id } : {}),
    })
      // Recarrega em vez de inserir otimista: o backend monta a Message com
      // a media_url do arquivo guardado, e adivinhar isso aqui daria uma
      // bolha diferente da que o polling traria logo depois.
      .then(() => { fetchMessages && fetchMessages(); })
      .catch(err => {
        alert(err?.response?.data?.detail
          || 'Não consegui enviar o arquivo. Tente de novo.');
      });
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedClient, deviceId]);

  // [Editar] — popula o input e devolve o controle pro agente. O envio segue
  // o caminho normal (Enter ou botão), sem atalho.
  const handleAiEditText = useCallback((text) => {
    aiInputSnapshotRef.current = text;
    setInputText(text);
  }, []);

  // ─── Composer: auto-grow do <textarea> tipo WhatsApp ───────────────────
  // Cada mudança em `inputText` recalcula a altura: zera e volta pro
  // scrollHeight, respeitando o cap definido em `.chat-composer-input`
  // (max-height do CSS/sx). useLayoutEffect pra o recálculo acontecer
  // ANTES do próximo paint — evita um frame com a caixa "curta" quando
  // o texto colado é grande.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    // Zera pra o scrollHeight refletir o conteúdo real (senão fica preso
    // no maior valor anterior).
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [inputText]);

  // ─── Envio de template (individual ou bulk) ─────────────────────────────
  // Aceita string (legado) ou objeto { name, language, components? }
  //
  // Set/26 (UX fix): extrai o corpo (BODY component) do template e envia
  // como `rendered_body` — o backend guarda em `messages.template_body`
  // e a bolha do chat mostra o texto real ao invés de "Template: {name}".
  //
  // Out/26: passa a mandar também a ESTRUTURA do template
  // (`template_struct`) — cabeçalho, rodapé e botões. Esta tela já listou
  // os templates pra pessoa escolher, então isso está na mão de graça; sem
  // mandar, o servidor teria que perguntar pra Meta a cada envio, que é a
  // consulta de rede que acabamos de tirar do caminho.
  //
  // O que o navegador NÃO manda é valor de variável: quem troca `{{1}}`
  // pelo nome do contato é o servidor, com o mesmo valor que foi pra Graph
  // API. Se a substituição fosse feita aqui, a bolha poderia mostrar uma
  // coisa e o cliente receber outra.
  const sendTemplateTo = async (clientPhone, template, templateVars = null, campanha = false, loteId = null, protecao = null) => {
    const tpl = typeof template === 'string' ? { name: template, language: 'pt_BR' } : template;
    // Extrai o texto do component BODY. Formato Meta:
    //   components: [{ type: 'HEADER', ...}, { type: 'BODY', text: '...' }, ...]
    const bodyComp = Array.isArray(tpl.components)
      ? tpl.components.find(c => (c?.type || '').toUpperCase() === 'BODY')
      : null;
    const renderedBody = bodyComp?.text || tpl.preview || null;
    return api.post('/messages/send', {
      device_id: deviceId,
      to: clientPhone,
      template_name: tpl.name,
      language: tpl.language,
      rendered_body: renderedBody,
      template_struct: Array.isArray(tpl.components) ? tpl.components : undefined,
      body: null,
      // Valores digitados na hora. O servidor resolve o resto pelo
      // mapeamento do template + dados do contato — o navegador não monta
      // parâmetro de Meta (out/26).
      template_vars: templateVars || undefined,
      // Só o lote marca campanha: com o teto de custo da Meta estourado e o
      // bloqueio ligado, o servidor recusa com 423. O template avulso pra
      // um cliente nunca é barrado (Administração Meta, out/26).
      campanha: campanha || undefined,
      // Um id por rodada de envio em massa (Formulários PR 3): agrupa as
      // respostas do formulário do botão por disparo na aba Respostas.
      lote_id: loteId || undefined,
      // Blindagem (out/26): opt-in confirmado e o que a tela mostrou —
      // `{ optin_confirmado, lote_total, lote_frios }`, só no lote.
      ...(protecao || {}),
    });
  };

  // Template com variável (out/26): antes de disparar, pergunta ao servidor
  // o que ele consegue resolver sozinho a partir do contato e o que falta.
  // Se resolve tudo, envia direto — perguntar o que já se sabe é atrito.
  const handleSendTemplateSingle = async (template) => {
    if (!selectedClient) return;
    const tpl = typeof template === 'string' ? { name: template, language: 'pt_BR' } : template;
    try {
      const pre = await api.post(`/templates/preflight/${deviceId}`, {
        template_name: tpl.name,
        language: tpl.language,
        client_ids: [selectedClient.id],
      });
      const alvo = pre.data?.contacts?.[0];
      if (pre.data?.has_variables && alvo && !alvo.ready) {
        // Falta valor: abre o diálogo em vez de disparar e tomar recusa da
        // Meta por parâmetro vazio.
        setVarsPrompt({ template: tpl, missing: alvo.missing, preview: alvo.preview });
        return;
      }
    } catch (err) {
      // Preflight indisponível não pode travar o envio de template SEM
      // variável, que é a maioria. Segue e deixa o servidor decidir.
      console.warn('preflight falhou, seguindo para o envio', err);
    }

    try {
      await sendTemplateTo(selectedClient.phone_number, tpl);
      fetchMessages();
      setShowTmplPanel(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Erro ao enviar template.');
    }
  };

  // Confirmação do diálogo de variáveis: reenvia com o que foi digitado.
  const handleConfirmVars = async (valores) => {
    if (!varsPrompt || !selectedClient) return;
    setSendingVars(true);
    try {
      await sendTemplateTo(selectedClient.phone_number, varsPrompt.template, valores);
      setVarsPrompt(null);
      fetchMessages();
      setShowTmplPanel(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Erro ao enviar template.');
    } finally {
      setSendingVars(false);
    }
  };

  // Blindagem (out/26): escolhido o template do lote, pergunta ao servidor
  // o que a faixa de proteção mostra. Trocar template ou seleção desmarca
  // o opt-in — a confirmação é daquele disparo, não de qualquer um.
  const chaveDaSelecao = useMemo(() => [...selectedConvIds].sort().join(','), [selectedConvIds]);
  useEffect(() => {
    setBulkPreflight(null);
    setBulkOptin(false);
    if (!showTmplPanel || !pickedTemplateName || !deviceId || !chaveDaSelecao) return undefined;
    const tpl = templates.find(t => t.name === pickedTemplateName);
    if (!tpl) return undefined;
    let vivo = true;
    api.post(`/templates/preflight/${deviceId}`, {
      template_name: tpl.name,
      language: tpl.language || 'pt_BR',
      client_ids: chaveDaSelecao.split(','),
    })
      .then(res => { if (vivo) setBulkPreflight(res.data); })
      .catch(() => { if (vivo) setBulkPreflight(null); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTmplPanel, pickedTemplateName, deviceId, chaveDaSelecao]);
  const bulkBloqueio = bloqueioDoDisparo(bulkPreflight);
  const podeEnviarLote = Boolean(pickedTemplateName) && !bulkSending && bulkOptin && !bulkBloqueio;

  // Bulk: envia o template selecionado pra todas as conversas marcadas
  // em paralelo (Promise.allSettled — falhas isoladas não derrubam o lote).
  const handleSendTemplateBulk = async () => {
    if (!pickedTemplateName || selectedConvIds.size === 0 || !podeEnviarLote) return;
    const template = templates.find(t => t.name === pickedTemplateName);
    if (!template) return;

    const targets = clients.filter(c => selectedConvIds.has(c.id));
    setBulkSending(true);
    setBulkResult(null);

    const loteId = novoLoteId();
    const protecao = {
      optin_confirmado: bulkOptin,
      lote_total: targets.length,
      lote_frios: bulkPreflight?.frios ?? null,
    };
    const results = await Promise.allSettled(
      targets.map(c => sendTemplateTo(c.phone_number, template, null, true, loteId, protecao))
    );
    const sent = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.length - sent;
    // Recusa que vale pro lote todo — teto de custo ou conta parada pela
    // Meta (423), opt-in (428), tema proibido (422), teto do teste (429):
    // mostra o texto do servidor em vez de "N falharam", que não diz o
    // que fazer.
    const bloqueio = results.find(r => r.status === 'rejected'
      && [422, 423, 428, 429].includes(r.reason?.response?.status));
    const motivo = bloqueio ? (bloqueio.reason.response.data?.detail || null) : null;

    setBulkSending(false);
    setBulkResult({ sent, failed, total: results.length, motivo });
    // Atualiza lista e limpa seleção
    fetchClients();
    if (failed === 0) {
      setTimeout(() => {
        setSelectedConvIds(new Set());
        setShowTmplPanel(false);
        setPickedTemplateName(null);
        setBulkResult(null);
      }, 2500);
    }
  };

  // ─── Pin/unpin de conversa (escopo: agente atual) ──────────────────────
  // Optimistic: muda o campo E re-ordena localmente pra a conversa subir
  // pro topo NA HORA. Depois chama a API e refaz o fetch pra reconciliar
  // (o backend é source of truth). Sem o re-sort local, a conversa só
  // mudava de posição no próximo fetch — dava impressão de que o pin
  // não funcionava.
  const togglePin = async (client) => {
    const willPin = !client.is_pinned;
    const nowIso = new Date().toISOString();
    // Optimistic: atualiza campo + re-sort com mesma regra do backend.
    setClients(prev => {
      const next = prev.map(c => (
        c.id === client.id
          ? { ...c, is_pinned: willPin, pinned_at: willPin ? nowIso : null }
          : c
      ));
      next.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        if (a.is_pinned && b.is_pinned) {
          const aPin = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
          const bPin = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
          return aPin - bPin;
        }
        const aTs = a?.last_message?.timestamp ? new Date(a.last_message.timestamp).getTime() : 0;
        const bTs = b?.last_message?.timestamp ? new Date(b.last_message.timestamp).getTime() : 0;
        return bTs - aTs;
      });
      return next;
    });
    try {
      if (willPin) {
        await api.post(`/conversations/pin/${client.id}`);
      } else {
        await api.delete(`/conversations/pin/${client.id}`);
      }
    } catch (err) {
      // Reverte optimistic e re-sincroniza
    }
    if (fetchClientsRef.current) fetchClientsRef.current();
  };

  // ─── Bulk delete: chama o endpoint de soft-delete das mensagens ─────────
  // Marca todas as msgs (deleted_at=NOW) daquelas conversas. O Client
  // continua no CRM/Contatos; se chegar msg nova o chat reaparece sozinho.
  // Optimistic update + refetch pra reconciliar com o backend (se algum
  // DELETE falhar, o refetch traz a conversa de volta).
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedConvIds);
    setClients(prev => prev.filter(c => !selectedConvIds.has(c.id)));
    setSelectedConvIds(new Set());
    setBulkDeleteConfirm(false);
    // Se estava com uma dessas aberta no chat, fecha.
    if (selectedClient && ids.includes(selectedClient.id)) {
      setSelectedClient(null);
    }
    await Promise.allSettled(
      ids.map(id => api.delete(`/conversations/device/${deviceId}/client/${id}`))
    );
    if (fetchClientsRef.current) fetchClientsRef.current();
  };

  // ─── Upload + áudio + video call (preservados da v2) ────────────────────
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    // Zera já: sem isso, escolher o MESMO arquivo depois de um erro não
    // dispara `onChange` e o agente acha que o botão parou de funcionar.
    event.target.value = '';
    if (!file || !selectedClient) return;

    const type = file.type.startsWith('image/') ? 'IMAGE'
               : file.type.startsWith('video/') ? 'VIDEO' : 'DOCUMENT';

    // Confere tamanho e, no vídeo, duração ANTES de subir (services/midia).
    // Antes daqui o arquivo subia inteiro pra ser recusado pelo Nginx (413)
    // ou pela Meta, e o agente só via "não foi possível enviar" depois da
    // espera — sem saber se o problema era o arquivo, a conexão ou o chip.
    const erro = await validarArquivo(file, type);
    if (erro) { alert(erro); return; }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('device_id', deviceId);
    formData.append('to', selectedClient.phone_number);
    formData.append('message_type', type);
    const citada = tirarCitacao();
    if (citada) formData.append('reply_to', citada.id);
    try {
      await api.post('/messages/send-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      fetchMessages();
    } catch (err) {
      if (err?.response?.status === 413) {
        alert('O arquivo ficou grande demais para o servidor. Reduza e tente de novo.');
        return;
      }
      // `catch {}` vazio até out/26. O backend passou a recusar mídia com
      // 423 (chip pausado pela Meta) e 400 (número inválido) — antes ele
      // mandava assim mesmo e a Meta engolia sem entregar. Com o catch
      // mudo, o agente anexava a foto, nada aparecia na tela, e ele
      // tentava de novo achando que tinha falhado o clique.
      alert(err?.response?.data?.detail || 'Não foi possível enviar o arquivo.');
    }
  };

  // ─── Gravação de voz ────────────────────────────────────────────────────
  //
  // Out/26, dois relatos do cliente:
  //
  //   "áudio longo não envia"  →  o MediaRecorder rodava SEM
  //     `audioBitsPerSecond`, então o navegador escolhia (~128 kbps no
  //     Chrome) e um minuto de fala passava de 1 MB — exatamente o
  //     `client_max_body_size` padrão do Nginx, que recusava com 413.
  //     Agora o bitrate é fixo em voz (~4 KB/s), o tamanho é previsível, e
  //     um contador na tela mostra quanto já foi gravado.
  //
  //   "não dá pra cancelar"  →  não dava mesmo: `onstop` SEMPRE enviava,
  //     não existia nenhum caminho que descartasse. O `cancelarRef` é esse
  //     caminho — `onstop` dispara igual (é o navegador quem chama), mas
  //     lê a flag antes de montar o upload.
  const cancelarRef = useRef(false);
  const streamRef = useRef(null);
  const limiteRef = useRef(null);

  // Tique do contador. Roda só enquanto grava, e é o `isRecording` que o
  // liga e desliga — sem intervalo pendurado quando a gravação acaba.
  useEffect(() => {
    if (!isRecording) return undefined;
    const t = setInterval(() => setSegundosGravando(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [isRecording]);

  const encerrarMicrofone = useCallback(() => {
    // Soltar as faixas é o que apaga a luzinha do microfone. Sem isso o
    // navegador segue indicando "gravando" depois de cancelar — e o agente,
    // com razão, acha que ainda estamos ouvindo.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (limiteRef.current) { clearTimeout(limiteRef.current); limiteRef.current = null; }
    audioChunks.current = [];
    setSegundosGravando(0);
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      cancelarRef.current = false;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm; codecs=opus') ? 'audio/webm; codecs=opus' : 'audio/webm';
      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: BITRATE_AUDIO,
      });
      audioChunks.current = [];
      mediaRecorder.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.current.push(e.data); };
      mediaRecorder.current.onstop = async () => {
        const cancelado = cancelarRef.current;
        const blob = new Blob(audioChunks.current, { type: 'audio/webm; codecs=opus' });
        encerrarMicrofone();
        // Cancelado, ou toque sem querer no botão: nada sai.
        if (cancelado || blob.size < 1000) return;

        const erro = await validarArquivo(blob, 'AUDIO');
        if (erro) { alert(erro); return; }

        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        formData.append('device_id', deviceId);
        formData.append('to', selectedClient.phone_number);
        formData.append('message_type', 'PTT');
        const citada = tirarCitacao();
        if (citada) formData.append('reply_to', citada.id);
        try {
          await api.post('/messages/send-ptt', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          fetchMessages();
        } catch (err) {
          // Mesmo motivo do /send-media acima. O 413 vem do Nginx, não do
          // app, então não tem `detail` — a mensagem própria evita um
          // "[object Object]" na cara do agente.
          const status = err?.response?.status;
          alert(
            status === 413
              ? 'O áudio ficou grande demais para o servidor. Grave um trecho mais curto.'
              : (err?.response?.data?.detail || 'Não foi possível enviar o áudio.')
          );
        }
      };
      mediaRecorder.current.start();
      setIsRecording(true);
      setSegundosGravando(0);
      // Teto duro: ao bater o limite a gravação PARA e o áudio segue pro
      // envio. Descartar aqui seria perder cinco minutos de fala de quem
      // não viu o contador.
      limiteRef.current = setTimeout(() => {
        if (mediaRecorder.current?.state === 'recording') {
          mediaRecorder.current.stop();
          setIsRecording(false);
        }
      }, MAX_SEGUNDOS_AUDIO * 1000);
    } catch { alert('Microfone bloqueado ou indisponível.'); }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      cancelarRef.current = false;
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder.current && isRecording) {
      cancelarRef.current = true;
      mediaRecorder.current.stop();   // `onstop` vê a flag e descarta
      setIsRecording(false);
    } else {
      encerrarMicrofone();
    }
  };

  const handleStartCall = async (type = 'video') => {
    if (!selectedClient || !deviceId) { alert('Selecione um cliente.'); return; }
    try {
      const res = await api.post('/jitsi/start-call', {
        client_id: selectedClient.id, device_id: deviceId, call_type: type,
      });
      if (res.data?.jitsi_url) {
        window.open(res.data.jitsi_url, '_blank', 'noopener,noreferrer');
        alert(`✅ Link de chamada enviado para ${selectedClient.name || selectedClient.phone_number}.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao iniciar chamada.');
    }
  };

  // ─── Nova conversa ──────────────────────────────────────────────────────
  const handleStartNewConv = async () => {
    setNewConvError('');
    if (!newClient.name || !newClient.phone) {
      setNewConvError('Preencha nome e número.');
      return;
    }
    const cleanPhone = newClient.phone.replace(/\D/g, '');
    if (!cleanPhone.startsWith('55') || cleanPhone.length < 12) {
      setNewConvError('Número deve estar no formato: 5521999999999 (com código do país)');
      return;
    }
    setNewConvLoading(true);
    try {
      const clientRes = await api.post('/clients/', {
        name: newClient.name, phone_number: cleanPhone, device_id: deviceId,
      });
      const created = clientRes.data;
      // Out/26: aqui saía sozinho o template `hello_world` (pt_BR) quando o
      // contato não tinha mensagem. É o template de exemplo da Meta — quase
      // sempre só existe em inglês, então falhava; e quando passava, era
      // cobrado no cartão do cliente sem ninguém ter escolhido enviar. A
      // conversa abre vazia e o agente escolhe o template.
      setOpenNewConvModal(false);
      setNewClient({ name: '', phone: '' });
      navigate(`/chat/${deviceId}/${created.id}`);
    } catch (err) {
      setNewConvError(err.response?.data?.detail || 'Erro ao criar cliente.');
    } finally {
      setNewConvLoading(false);
    }
  };

  // ─── Helpers de render ──────────────────────────────────────────────────
  const urlDaMidia = (msg) => {
    const mediaPath = msg.media_url || msg.file_path || msg.url;
    if (!mediaPath) return null;
    const baseUrl = process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br';
    return mediaPath.startsWith('http') ? mediaPath : `${baseUrl}${mediaPath}`;
  };
  // 'imagem' | 'audio' | 'video' | 'arquivo' | null (texto)
  const tipoDaMidia = (msg) => {
    const mediaPath = msg.media_url || msg.file_path || msg.url;
    if (!mediaPath) return null;
    const type = (msg.message_type || '').toUpperCase();
    if (type === 'IMAGE' || type === 'STICKER' || mediaPath.match(/\.(jpeg|jpg|gif|png|webp)$/i)) return 'imagem';
    if (type === 'AUDIO' || type === 'PTT' || mediaPath.match(/\.(ogg|mp3|wav|webm|m4a)$/i)) return 'audio';
    if (type === 'VIDEO' || mediaPath.match(/\.(mp4|mov|avi)$/i)) return 'video';
    return 'arquivo';
  };
  // Texto da mensagem, sem a bolha. Templates: prefere o corpo renderizado
  // (set/26); mensagem antiga anterior ao redesign cai no `content` legacy
  // ("Template: {name}"). Nunca fica em branco.
  const textoDaMensagem = (msg) => {
    const isTemplate = (msg.message_type || '').toUpperCase() === 'TEMPLATE'
      || (msg.content || '').startsWith('Template: ');
    return (isTemplate && msg.template_body) ? msg.template_body : msg.content;
  };
  const renderMessageContent = (msg, isOut = false) => {
    const fullUrl = urlDaMidia(msg);
    const tipo = tipoDaMidia(msg);
    if (tipo === 'imagem') {
      return <Box component="img" src={fullUrl} alt={msg.content || 'Imagem'}
        sx={{ display: 'block', maxWidth: { xs: 240, sm: 280 }, maxHeight: 320, borderRadius: '14px', cursor: 'pointer' }}
        onClick={() => window.open(fullUrl)} />;
    }
    if (tipo === 'audio') return <AudioBolha src={fullUrl} enviada={isOut} />;
    if (tipo === 'video') {
      return <Box component="video" src={fullUrl} controls sx={{ display: 'block', maxWidth: { xs: 240, sm: 280 }, maxHeight: 320, borderRadius: '14px', bgcolor: '#000' }} />;
    }
    if (tipo === 'arquivo') {
      return (
        <Box component="a" href={fullUrl} target="_blank" rel="noopener noreferrer" sx={{
          display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 200, textDecoration: 'none', color: 'inherit',
          '&:hover .doc-nome': { textDecoration: 'underline' },
        }}>
          <Box component="span" sx={{
            width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: isOut ? C.obB : C.inset, color: isOut ? C.obT : C.text2,
          }}>
            <FiFileText size={17} aria-hidden />
          </Box>
          <Box component="span" sx={{ minWidth: 0 }}>
            <Box component="span" className="doc-nome" sx={{ display: 'block', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
              {(msg.content && !msg.content.startsWith('[')) ? msg.content : 'Documento'}
            </Box>
            <Box component="span" sx={{ display: 'block', fontSize: 12, color: isOut ? C.obM : C.cap }}>Abrir arquivo</Box>
          </Box>
        </Box>
      );
    }
    return (
      <Typography component="span" sx={{ fontFamily: FONT_TXT, fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {textoDaMensagem(msg)}
      </Typography>
    );
  };

  // Hora + risquinhos, dentro da bolha (out/26, redesign). Antes ficavam
  // numa linha embaixo de cada bolha e dobravam a altura da conversa.
  const renderMeta = (msg, isOut) => {
    const cor = isOut ? C.obM : C.cap;
    return (
      <Box component="span" sx={{
        display: 'inline-flex', alignItems: 'center', gap: '3px', fontFamily: FONT_NUM,
        fontSize: 11, lineHeight: 1, color: cor, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      }}>
        {format(new Date(msg.timestamp), 'HH:mm')}
        {renderStatusIcon(msg, cor)}
      </Box>
    );
  };

  // ─── A bolha de template, como o cliente recebeu ──────────────────────
  // Out/26. Antes daqui a bolha mostrava só o corpo CRU: `Olá {{1}},`
  // literal, sem imagem e sem botões. O agente não conseguia conferir o
  // que saiu, e um print da conversa não batia com o WhatsApp do cliente.
  //
  // Tudo vem de `template_render`, congelado no envio pelo servidor — não
  // é remontado a partir do template de hoje, que pode ter sido editado
  // depois. Mensagem antiga não tem a coluna e cai no corpo de sempre.
  const renderTemplateFiel = (msg, meta = null) => {
    const r = msg.template_render;
    const linhaMeta = (rodape) => (meta || rodape) ? (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: 1 }}>
        <Typography sx={{ fontFamily: FONT_TXT, fontSize: 12, color: C.cap, whiteSpace: 'pre-wrap' }}>{rodape}</Typography>
        {meta}
      </Box>
    ) : null;
    if (!r) {
      return (
        <>
          <Typography sx={{ fontFamily: FONT_TXT, fontSize: 'inherit', lineHeight: 1.45, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {textoDaMensagem(msg)}
          </Typography>
          {linhaMeta(null)}
        </>
      );
    }

    const imagem = r.header_image
      ? (r.header_image.startsWith('http')
          ? r.header_image
          : `${process.env.REACT_APP_API_URL || ''}${r.header_image}`)
      : null;

    return (
      <>
        {imagem && (
          <Box
            component="img"
            src={imagem}
            alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            sx={{
              display: 'block', width: '100%', maxWidth: 280, maxHeight: 200,
              objectFit: 'cover', borderRadius: '8px', mb: 1, bgcolor: C.inset,
            }}
          />
        )}
        {/* Cabeçalho de texto: negrito, como a Meta desenha. */}
        {r.header_text && (
          <Typography sx={{
            fontFamily: FONT_TXT, fontSize: 'inherit', fontWeight: 700, color: C.text,
            lineHeight: 1.4, mb: 0.4, whiteSpace: 'pre-wrap',
          }}>
            {r.header_text}
          </Typography>
        )}
        <Typography sx={{
          fontFamily: FONT_TXT, fontSize: 'inherit', lineHeight: 1.45, color: C.text,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {r.body || msg.template_body || msg.content}
        </Typography>
        {/* Rodapé do modelo à esquerda, hora e risquinhos à direita. */}
        {linhaMeta(r.footer || null)}
        {/* Botões: desenhados, não clicáveis. Quem clica é o cliente no
            WhatsApp dele — um botão que responde aqui faria o agente achar
            que disparou alguma coisa. */}
        {Array.isArray(r.buttons) && r.buttons.length > 0 && (
          <Box sx={{ mt: 1.2, borderTop: `1px solid ${C.bdr}` }}>
            {r.buttons.map((b, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 0.7, py: '8px', color: C.blue,
                  fontFamily: FONT_TXT, fontSize: 13.5, fontWeight: 600,
                  borderTop: i > 0 ? `1px solid ${C.bdr}` : 'none',
                }}
              >
                {b.type === 'URL' && <FiExternalLink size={13} />}
                {b.type === 'PHONE_NUMBER' && <FiPhone size={13} />}
                {b.type === 'FLOW' && <FiClipboard size={13} />}
                {b.text}
              </Box>
            ))}
          </Box>
        )}
      </>
    );
  };

  // ─── Formulário (WhatsApp Flows), out/26 ────────────────────────────────
  // O envio chega marcado em `template_render.formulario` e a resposta em
  // `template_render.formulario_resposta` (services/formulario_envio) — a
  // mesma coluna do template, pra `messages` não precisar de migration. A
  // bolha desenha o que o cliente recebeu (texto + botão) e, na resposta,
  // as perguntas e o que foi pro CRM.
  const CORES_FORM = {
    texto: C.text, texto2: C.text2, texto3: C.text3, borda: C.bdr,
    fundo: C.inset, verde: C.greenD, ambar: C.amberD,
  };
  const renderFormulario = (msg, isOut, raio = null) => {
    const env = msg.template_render?.formulario;
    const resp = msg.template_render?.formulario_resposta;
    const dados = env || resp;
    const teste = Boolean(dados?.teste);
    return (
      <Box sx={{
        bgcolor: C.card, border: `1px solid ${C.blueB}`,
        borderRadius: raio || (isOut ? '18px 18px 5px 18px' : '18px 18px 18px 5px'),
        p: '12px 14px 10px', boxShadow: C.sh1, minWidth: { xs: 0, sm: 240 },
      }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: C.blueD, mb: 0.8, fontFamily: FONT_TXT, display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap',
        }}>
          <FiClipboard size={11} />
          {env ? 'Formulário enviado' : 'Formulário respondido'}
          {dados?.nome && <Box component="span" sx={{ textTransform: 'none', letterSpacing: 0, fontSize: '0.7rem', color: C.text2 }}>· {dados.nome}</Box>}
          {teste && (
            <Box component="span" sx={{ letterSpacing: '0.06em', bgcolor: C.amberBg, color: C.amberD, border: `1px solid ${C.amberB}`, borderRadius: '4px', px: 0.6 }}>
              Teste
            </Box>
          )}
        </Typography>
        {env ? (
          <>
            <Typography sx={{ fontFamily: FONT_TXT, fontSize: 'inherit', lineHeight: 1.45, color: C.text, whiteSpace: 'pre-wrap' }}>{env.texto}</Typography>
            {/* Desenhado, não clicável: quem abre o formulário é o cliente. */}
            <Box sx={{
              mt: 1, pt: 1, borderTop: `1px solid ${C.bdr}`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 0.7, color: C.blue, fontFamily: FONT_TXT, fontSize: 13.5, fontWeight: 600,
            }}>
              <FiClipboard size={13} /> {env.botao}
            </Box>
          </>
        ) : (
          <RespostasLista respostas={resp.respostas} crm={resp.crm} teste={teste} cores={CORES_FORM} />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.8 }}>{renderMeta(msg, false)}</Box>
      </Box>
    );
  };

  // Set/26: até esta data `status` nunca era escrito — o handler de
  // `statuses` do webhook logava e descartava —, então TODA mensagem caía
  // no ✓ do final e ficava "enviada" pra sempre. Inclusive a que a Meta
  // tinha recusado: o agente só descobria pelo cliente reclamando que
  // nunca recebeu. Os riscos abaixo já existiam; o que faltava era o
  // backend gravar, e o caso FAILED aqui.
  const renderStatusIcon = (msg, cor = C.cap) => {
    if (msg.direction !== 'OUTBOUND') return null;
    if (msg.status === 'FAILED') {
      return (
        <Tooltip title={msg.status_error || 'A Meta recusou a entrega desta mensagem.'}>
          <Box component="span" sx={{ display: 'inline-flex', ml: '2px', color: C.red }}>
            <FiAlertTriangle size={12} aria-label="Não entregue" />
          </Box>
        </Tooltip>
      );
    }
    if (msg.status === 'READ') return <IoCheckmarkDoneOutline size={15} color="var(--ch-blue)" aria-label="Lida" />;
    if (msg.status === 'DELIVERED') return <IoCheckmarkDoneOutline size={15} color={cor} aria-label="Entregue" />;
    return <IoCheckmarkOutline size={15} color={cor} aria-label="Enviada" />;
  };

  // ─── Selo de reações, grudado na bolha (out/26) ───────────────────────
  // Como no WhatsApp: a reação não é uma bolha na timeline, é um selo na
  // mensagem reagida. Antes disto a reação recebida virava uma Message com
  // content="Reagiu a uma mensagem" — uma bolha solta que não dizia o quê
  // nem a quê, porque o emoji e o alvo eram descartados no webhook.
  //
  // O selo é CLICÁVEL e abre o painel: é por ele que o agente troca ou
  // tira a própria reação, e é onde a mão já está.
  //
  // `mt: -10px` encaixa o selo mordendo a borda de baixo da bolha. Sem a
  // sobreposição ele parece uma segunda mensagem minúscula — justamente a
  // leitura que este trabalho veio desfazer.
  const renderReacoes = (msg, isOut) => {
    const daPessoa = msg.reaction_in || null;
    const nossa = msg.reaction_out || null;
    if (!daPessoa && !nossa) return null;

    const nomeContato = selectedClient?.name || 'O contato';
    const legenda = [
      daPessoa && `${nomeContato} reagiu com ${daPessoa}`,
      nossa && `Sua equipe reagiu com ${nossa}`,
    ].filter(Boolean).join(' · ');

    return (
      <Box sx={{
        display: 'flex', justifyContent: isOut ? 'flex-end' : 'flex-start',
        mt: '-8px', mb: '2px', mx: '10px', position: 'relative', zIndex: 1,
      }}>
        <Tooltip title={`${legenda}. Clique para reagir.`}>
          <Box
            component="button"
            type="button"
            aria-label={legenda}
            onClick={e => setReacaoAlvo({ msg, anchorEl: e.currentTarget })}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              bgcolor: C.card, border: `1px solid ${C.bdr}`,
              borderRadius: 999, px: '4px', py: '2px',
              boxShadow: C.sh1,
              fontSize: '0.78rem', lineHeight: 1.1, cursor: 'pointer',
              font: 'inherit', fontFamily: FONT_TXT,
              transition: 'border-color 0.15s',
              '&:hover': { borderColor: C.bdr2 },
              '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 1 },
            }}
          >
            {daPessoa ? <Box component="span" sx={{ fontSize: '0.86rem' }}>{daPessoa}</Box> : null}
            {/* A nossa ganha o anel verde da marca pra o agente saber, de
                relance, qual das duas é dele — sem precisar do tooltip. */}
            {nossa ? (
              <Box component="span" sx={{
                fontSize: '0.86rem', bgcolor: C.greenBg,
                border: `1px solid ${C.greenB}`, borderRadius: '20px',
                px: '3px', display: 'inline-flex',
              }}>
                {nossa}
              </Box>
            ) : null}
          </Box>
        </Tooltip>
      </Box>
    );
  };

  // Gatilho de "reagir" que aparece ao lado da bolha no hover. Fica FORA da
  // bolha pra não disputar espaço com o texto, e do lado de dentro da
  // conversa (esquerda nas nossas, direita nas do contato) porque é pra lá
  // que o cursor já está indo.
  //
  // `opacity` em vez de montar/desmontar: a troca de visibilidade no hover
  // não pode mexer no layout, ou a bolha salta quando o mouse passa.
  const renderBotaoReagir = (msg) => {
    // Sem wamid a Meta não tem a que referenciar a reação — é o caso de
    // mensagem que falhou no envio. O backend recusa com 409, então o
    // botão nem aparece em vez de prometer o que não cumpre.
    if (!msg?.whatsapp_message_id) return null;
    const aberto = reacaoAlvo?.msg?.id === msg.id;
    return (
      <Tooltip title="Reagir">
        <Box
          component="button"
          type="button"
          aria-label="Reagir a esta mensagem"
          aria-expanded={aberto}
          onClick={e => setReacaoAlvo(
            aberto ? null : { msg, anchorEl: e.currentTarget },
          )}
          sx={botaoAcaoSx}
        >
          <FiSmile size={14} />
        </Box>
      </Tooltip>
    );
  };

  // ─── Ações da bolha (nov/26): reagir, responder, encaminhar ──────────
  // No computador aparecem no hover, do lado de DENTRO da conversa. No
  // celular não existe hover: um botão "⋯" discreto (e o toque longo na
  // bolha) abre uma folha com as três — três botões sempre visíveis por
  // bolha poluíam a conversa.
  //
  // `opacity` em vez de montar/desmontar: a troca de visibilidade no hover
  // não pode mexer no layout, ou a bolha salta quando o mouse passa.
  const botaoAcaoSx = {
    width: 28, height: 28, flexShrink: 0, p: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '50%', cursor: 'pointer',
    border: `1px solid ${C.bdr}`, bgcolor: C.card, color: C.cap, boxShadow: C.sh1,
    transition: 'color 0.15s, border-color 0.15s',
    '&:hover': { color: C.greenD, borderColor: C.greenB },
    '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 1 },
  };
  const iniciarResposta = (msg) => {
    setRespondendo(msg);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const renderAcoes = (msg) => {
    const responder = podeResponder(msg);
    const encaminhar = podeEncaminhar(msg);
    const reagir = Boolean(msg?.whatsapp_message_id);
    if (!responder && !encaminhar && !reagir) return null;
    const aberto = reacaoAlvo?.msg?.id === msg.id || acoesMsg?.msg?.id === msg.id;
    if (isMobile) {
      return (
        <Box component="button" type="button"
          aria-label="Ações da mensagem" aria-haspopup="dialog"
          onClick={e => setAcoesMsg({ msg, anchorEl: e.currentTarget })}
          sx={{
            width: 28, height: 28, flexShrink: 0, p: 0, alignSelf: 'center', border: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%',
            bgcolor: 'transparent', color: C.cap, opacity: aberto ? 1 : 0.75, cursor: 'pointer',
            '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 1 },
          }}>
          <FiMoreHorizontal size={16} />
        </Box>
      );
    }
    return (
      <Box className="acoes-bolha" sx={{
        display: 'flex', gap: '4px', alignSelf: 'center', flexShrink: 0,
        opacity: aberto ? 1 : 0, transition: 'opacity 0.15s',
        '&:focus-within': { opacity: 1 },
      }}>
        {reagir && renderBotaoReagir(msg)}
        {responder && (
          <Tooltip title="Responder">
            <Box component="button" type="button" aria-label="Responder a esta mensagem"
              onClick={() => iniciarResposta(msg)} sx={botaoAcaoSx}>
              <FiCornerUpLeft size={14} />
            </Box>
          </Tooltip>
        )}
        {encaminhar && (
          <Tooltip title="Encaminhar">
            <Box component="button" type="button" aria-label="Encaminhar esta mensagem" aria-haspopup="dialog"
              onClick={() => setEncaminhando(msg)} sx={botaoAcaoSx}>
              <FiCornerUpRight size={14} />
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Toque longo na bolha (celular) abre a mesma folha de ações.
  const toqueLongoBolha = (msg) => (isMobile ? {
    onTouchStart: (e) => {
      const alvo = e.currentTarget;
      clearTimeout(toqueBolhaRef.current.timer);
      toqueBolhaRef.current.timer = setTimeout(() => {
        try { navigator.vibrate?.(12); } catch { /* sem vibração */ }
        setAcoesMsg({ msg, anchorEl: alvo });
      }, 500);
    },
    onTouchMove: () => clearTimeout(toqueBolhaRef.current.timer),
    onTouchEnd: () => clearTimeout(toqueBolhaRef.current.timer),
    onTouchCancel: () => clearTimeout(toqueBolhaRef.current.timer),
  } : {});

  // Clicar na citação leva até a mensagem citada, se ela já está na tela.
  const irParaMensagem = (id) => {
    const alvo = id && document.getElementById(`msg-${id}`);
    if (!alvo) {
      setAviso(messagesHasMore
        ? 'A mensagem citada é mais antiga. Use "Ver mais 20 mensagens" no topo para chegar até ela.'
        : 'A mensagem citada não está nesta conversa.');
      return;
    }
    const reduzir = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    alvo.scrollIntoView({ block: 'center', behavior: reduzir ? 'auto' : 'smooth' });
    setDestacada(id);
    setTimeout(() => setDestacada(atual => (atual === id ? null : atual)), 1600);
  };

  // "Encaminhada" e a citação, no topo da bolha — como no WhatsApp.
  const nomeDoContato = selectedClient?.name || formatarFone(selectedClient?.phone_number || '');
  const renderCitacao = (c, isOut, { naCaixa = false } = {}) => {
    if (!c) return null;
    const cor = c.de === 'out' ? C.greenD : C.blueD;
    const autor = !c.disponivel ? 'Mensagem citada' : (c.de === 'out' ? 'Você' : nomeDoContato);
    const conteudo = (
      <>
        <Box component="span" aria-hidden sx={{ width: 4, flexShrink: 0, bgcolor: c.disponivel ? cor : C.bdr2 }} />
        <Box component="span" sx={{ py: '6px', pr: '10px', minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Box component="span" sx={{ fontSize: 12.5, fontWeight: 700, color: c.disponivel ? cor : C.cap, lineHeight: 1.35 }}>
            {naCaixa ? `Respondendo a ${autor === 'Você' ? 'você' : autor}` : autor}
          </Box>
          <Box component="span" sx={{
            fontSize: 13, lineHeight: 1.4, color: isOut ? C.obM : C.text2,
            fontStyle: c.disponivel ? 'normal' : 'italic',
            display: '-webkit-box', WebkitLineClamp: naCaixa ? 1 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            overflowWrap: 'anywhere',
          }}>
            {c.disponivel ? (c.texto || '…') : 'Mensagem não encontrada'}
          </Box>
        </Box>
        {c.miniatura && (
          <Box component="img" src={c.miniatura} alt="" sx={{ width: 44, height: 44, objectFit: 'cover', flexShrink: 0, alignSelf: 'center', mr: naCaixa ? 0 : '4px', borderRadius: '6px' }} />
        )}
      </>
    );
    if (naCaixa) return conteudo;
    const sx = {
      display: 'flex', alignItems: 'stretch', gap: '8px', width: '100%', minWidth: 180,
      mb: '6px', p: 0, border: 0, borderRadius: '10px', overflow: 'hidden', textAlign: 'left',
      bgcolor: 'var(--ch-cita)', font: 'inherit', color: 'inherit',
    };
    return c.disponivel ? (
      <Box component="button" type="button" onClick={() => irParaMensagem(c.id)}
        aria-label={`Ir para a mensagem citada: ${c.texto || ''}`}
        sx={{ ...sx, cursor: 'pointer', '&:hover': { filter: 'brightness(0.97)' },
          '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 1 } }}>
        {conteudo}
      </Box>
    ) : (
      <Box sx={sx}>{conteudo}</Box>
    );
  };
  const renderTopoDaBolha = (msg, isOut) => (
    <>
      {msg.forwarded && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: '4px', mb: '4px',
          fontSize: 12, fontStyle: 'italic', color: isOut ? C.obM : C.cap,
        }}>
          <FiCornerUpRight size={12} aria-hidden /> Encaminhada
        </Box>
      )}
      {renderCitacao(msg.citacao, isOut)}
    </>
  );

  // O motivo da falha, embaixo da bolha. Não basta o ícone: no celular não
  // existe hover pra ver tooltip, e "não entregue" sem motivo não diz se é
  // reenviar, corrigir a imagem ou chamar o suporte. O texto vem pronto em
  // português do backend (services/erros_entrega).
  const renderFalhaEntrega = (msg) => {
    if (msg.direction !== 'OUTBOUND' || msg.status !== 'FAILED') return null;
    return (
      <Box
        role="status"
        sx={{
          display: 'flex', alignItems: 'flex-start', gap: '6px', mt: '6px',
          bgcolor: C.redBg, border: `1px solid ${C.redB}`, borderRadius: '12px',
          px: '10px', py: '7px', maxWidth: '100%',
        }}
      >
        <Box component="span" sx={{ color: C.red, display: 'flex', mt: '2px', flexShrink: 0 }}>
          <FiAlertTriangle size={13} />
        </Box>
        <Typography sx={{
          fontFamily: FONT_TXT, fontSize: 12.5, lineHeight: 1.45,
          color: C.redD, fontWeight: 600,
        }}>
          Não chegou ao cliente.{' '}
          <Box component="span" sx={{ fontWeight: 400 }}>
            {msg.status_error || 'A Meta recusou a entrega desta mensagem.'}
          </Box>
        </Typography>
      </Box>
    );
  };

  const getLastMessagePreview = (client) => {
    const last = client?.last_message;
    // Sem last_message = contato do CRM que ainda não teve conversa iniciada.
    // Só acontece quando o filtro 'Novos' trouxe unmessaged pela flag do
    // backend. Preview específico deixa claro que não é bug — é lead novo.
    //
    // Ícones de traço (out/26) no lugar dos emojis 📷 🎵 📎: emoji muda de
    // desenho e de peso em cada sistema e não acompanha a cor do texto.
    if (!last) return { Icone: FiClock, label: 'Aguardando primeiro contato' };
    const type = (last.message_type || 'TEXT').toString().toUpperCase();
    const text = (last.content || '').trim();
    if (type === 'IMAGE')   return { Icone: FiCamera, label: text || 'Foto' };
    if (type === 'VIDEO')   return { Icone: FiVideo, label: text || 'Vídeo' };
    if (type === 'AUDIO' || type === 'PTT') return { Icone: FiMic, label: 'Mensagem de voz' };
    if (type === 'DOCUMENT') return { Icone: FiFileText, label: text || 'Documento' };
    if (type === 'STICKER') return { Icone: FiSmile, label: 'Figurinha' };
    if (type === 'TEMPLATE') {
      // Prefere o corpo renderizado (set/26). Fallback pra "Template
      // enviado" quando é uma msg antiga sem `template_body`.
      const label = (last.template_body || text || 'Template enviado').trim();
      return { Icone: null, label, isTemplate: true };
    }
    return { Icone: null, label: text || '' };
  };

  const formatLastMsgTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return format(d, 'HH:mm', { locale: ptBR });
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
    return format(d, 'dd/MM', { locale: ptBR });
  };

  // ─── Derivados (counts + lista filtrada) ────────────────────────────────
  // Janela para considerar um contato "novo" por idade. 3 dias em ms.
  const NEW_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

  const enrichedClients = useMemo(() => {
    const nowMs = Date.now();
    return clients.map(c => {
      const backendUnread = Number(c.unread_count || 0);
      const localUnread = Number(unreadByClient[c.id] || 0);
      const unread = Math.max(backendUnread, localUnread);
      const lm = c.last_message;
      const isTemplate = (lm?.message_type || '').toUpperCase() === 'TEMPLATE';
      // Chip virtual "Novo": vale se o contato foi criado há < 3 dias OU
      // se ainda não recebeu nenhuma msg OUTBOUND nossa. Regra derivada,
      // não persiste — some sozinha quando as condições deixam de valer.
      const createdMs = c.created_at ? new Date(c.created_at).getTime() : 0;
      const isRecent = createdMs > 0 && (nowMs - createdMs) < NEW_WINDOW_MS;
      const isNew = isRecent || c.has_outbound === false;
      return { ...c, unread, isTemplate, isNew };
    });
  }, [clients, unreadByClient]);

  const counts = useMemo(() => {
    // Contadores por etiqueta e por status CRM — alimentam os badges
    // numéricos ao lado de cada item nos dropdowns.
    const c = {
      all: enrichedClients.length,
      unread: 0,
      new: 0,
      byLabel: {},     // labelId → count
      byFolder: {},    // folderId → count
      byInterest: { ALTO: 0, MEDIO: 0, BAIXO: 0, FRIO: 0 },
    };
    enrichedClients.forEach(cl => {
      if (cl.unread > 0) c.unread++;
      if (cl.isNew) c.new++;
      (cl.labels || []).forEach(l => {
        c.byLabel[l.id] = (c.byLabel[l.id] || 0) + 1;
      });
      (cl.folders || []).forEach(f => {
        c.byFolder[f.id] = (c.byFolder[f.id] || 0) + 1;
      });
      const it = (cl.interest_status || '').toUpperCase();
      if (c.byInterest[it] !== undefined) c.byInterest[it]++;
    });
    return c;
  }, [enrichedClients]);

  const filteredClients = useMemo(() => {
    let list = enrichedClients.filter(c =>
      (c.name || c.phone_number || '').toLowerCase().includes(searchClients.toLowerCase())
    );
    if (filterTab === 'unread') {
      list = list.filter(c => c.unread > 0);
    } else if (filterTab === 'new') {
      list = list.filter(c => c.isNew);
    } else if (typeof filterTab === 'object' && filterTab?.type === 'label') {
      list = list.filter(c => (c.labels || []).some(l => l.id === filterTab.id));
    } else if (typeof filterTab === 'object' && filterTab?.type === 'interest') {
      list = list.filter(c => (c.interest_status || '').toUpperCase() === filterTab.value);
    } else if (typeof filterTab === 'object' && filterTab?.type === 'folder') {
      // Mesmo formato de `labels`: a lista de conversas traz as pastas de
      // cada contato (conversations.py). Um contato pode estar em mais de
      // uma, então é `some`, não igualdade.
      list = list.filter(c => (c.folders || []).some(f => f.id === filterTab.id));
    }
    return list;
  }, [enrichedClients, searchClients, filterTab]);

  // Reset da paginação quando o escopo muda (nova busca, novo filtro).
  // Motivo: se o user paginou pra ver 90 conversas e depois filtra por
  // "Alto interesse", mostrar 90 do novo filtro seria surpresa — mais
  // intuitivo começar de novo com 50 e ele decide se quer ver mais.
  useEffect(() => {
    setConversationsVisible(CONVERSATIONS_PAGE_SIZE);
  }, [filterTab, searchClients]);

  // Fatia da lista efetivamente renderizada. Filtros/busca continuam
  // operando sobre a lista COMPLETA (enrichedClients) — só o slice final
  // é o que vai pro DOM.
  const visibleClients = useMemo(
    () => filteredClients.slice(0, conversationsVisible),
    [filteredClients, conversationsVisible],
  );
  const conversationsHasMore = filteredClients.length > conversationsVisible;

  // ─── Multi-select helpers ───────────────────────────────────────────────
  const toggleConvSelected = (id) => {
    setSelectedConvIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => {
    setSelectedConvIds(new Set());
    setShowTmplPanel(false);
    setPickedTemplateName(null);
    setBulkResult(null);
  };
  const toggleSelectAll = () => {
    if (selectedConvIds.size === filteredClients.length && filteredClients.length > 0) {
      setSelectedConvIds(new Set());
    } else {
      setSelectedConvIds(new Set(filteredClients.map(c => c.id)));
    }
  };
  const selectAllState = selectedConvIds.size === 0
    ? 'none'
    : selectedConvIds.size === filteredClients.length && filteredClients.length > 0
      ? 'all'
      : 'partial';

  // Nome resumido das conversas selecionadas pro side-panel
  const selectedNames = useMemo(() => {
    const names = clients.filter(c => selectedConvIds.has(c.id)).map(c => c.name || c.phone_number);
    if (names.length <= 3) return names.join(' · ');
    return `${names.slice(0, 3).join(' · ')} · +${names.length - 3}`;
  }, [clients, selectedConvIds]);

  // ─── Tema claro/escuro ──────────────────────────────────────────────────
  // `data-theme` vai no <html> (e não num wrapper da página) porque Dialog,
  // Menu e Popover do MUI renderizam por portal em document.body: dentro
  // deles as `var(--ch-*)` só resolvem se estiverem em :root.
  //
  // O atributo é REMOVIDO ao desmontar. Sem isso ele ficaria pendurado no
  // documento depois de sair do chat — inofensivo hoje (nenhuma outra
  // página lê `--ch-*`), mas é o tipo de resíduo global que morde quando
  // alguém adicionar dark mode em outra tela usando o mesmo atributo.
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem('cetto:chat-theme') === 'dark' ? 'dark' : 'light';
    } catch {
      // Modo privativo / storage bloqueado: cai no claro em vez de quebrar.
      return 'light';
    }
  });

  // useLayoutEffect e não useEffect: aplica o atributo ANTES da pintura,
  // senão quem escolheu escuro vê um flash branco a cada entrada no chat.
  useLayoutEffect(() => {
    const el = document.documentElement;
    if (themeMode === 'dark') el.setAttribute('data-theme', 'dark');
    else el.removeAttribute('data-theme');
    return () => el.removeAttribute('data-theme');
  }, [themeMode]);

  const toggleTheme = useCallback(() => {
    setThemeMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('cetto:chat-theme', next); } catch { /* storage indisponível */ }
      return next;
    });
  }, []);

  // ─── Coluna da direita e etiquetas (out/26, redesign) ───────────────────
  const fecharPainelTemplates = () => {
    setShowTmplPanel(false); setPickedTemplateName(null); setBulkResult(null);
  };

  // O MESMO popover de etiquetas em três lugares (linha da lista, topo da
  // conversa e mini-página do contato) — só muda onde ele ancora.
  const propsPopoverEtiquetas = (client) => ({
    client,
    allLabels,
    labelSearch,
    setLabelSearch,
    creatingLabel,
    setCreatingLabel,
    newLabelColor,
    setNewLabelColor,
    onToggle: (labelId) => toggleLabelOnClient(client.id, labelId),
    onCreate: async (name) => {
      const created = await createNewLabel(name, newLabelColor, client.id);
      if (created) {
        setLabelSearch('');
        setCreatingLabel(false);
      }
    },
    onDelete: deleteLabel,
    onClose: () => { setLabelPopoverClientId(null); setEtiquetaNoPainel(false); },
  });

  // A conversa aberta, lida da LISTA (fonte de verdade das etiquetas, do
  // perfil e da qualificação — `selectedClient` é a cópia do clique).
  const clienteAberto = selectedClient
    ? (clients.find(c => c.id === selectedClient.id) || selectedClient)
    : null;
  const popoverForaDaLista = Boolean(clienteAberto)
    && etiquetaNoPainel && labelPopoverClientId === clienteAberto.id;

  // Etiquetas na mini-página: pílulas maiores, mesmo clique (remove) e o
  // mesmo popover, aberto numa linha própria embaixo delas.
  const etiquetasDoPainel = clienteAberto ? (
    <>
      {(clienteAberto.labels || []).map(lbl => (
        <LabelPill key={lbl.id} grande label={lbl}
          onClick={() => toggleLabelOnClient(clienteAberto.id, lbl.id)} />
      ))}
      <AddLabelChip grande onClick={() => {
        setEtiquetaNoPainel(!popoverForaDaLista);
        setLabelPopoverClientId(popoverForaDaLista ? null : clienteAberto.id);
        setLabelSearch('');
        setCreatingLabel(false);
      }} />
      {popoverForaDaLista && (
        <Box sx={{ position: 'relative', flexBasis: '100%', height: 0 }}>
          <LabelPopover {...propsPopoverEtiquetas(clienteAberto)} esquerda={0} />
        </Box>
      )}
    </>
  ) : null;

  const CATEGORIA_TEMPLATE = { MARKETING: 'Marketing', UTILITY: 'Utilidade', AUTHENTICATION: 'Autenticação' };

  // O painel de templates — coluna no computador, folha no celular.
  const renderPainelTemplates = (folha) => {
    const emLote = selectedConvIds.size > 0;
    const caixaAviso = (tom, texto) => (
      <Box role="status" sx={{
        p: '10px 12px', borderRadius: '12px', fontFamily: FONT_TXT, fontSize: 13, lineHeight: 1.5,
        bgcolor: tom === 'ok' ? C.greenBg : tom === 'erro' ? C.redBg : C.amberBg,
        border: `1px solid ${tom === 'ok' ? C.greenB : tom === 'erro' ? C.redB : C.amberB}`,
        color: tom === 'ok' ? C.greenD : tom === 'erro' ? C.redD : C.amberD,
      }}>
        {texto}
      </Box>
    );
    return (
      <>
        <Box sx={folha
          ? { height: 52, px: '18px', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }
          : { height: 68, pl: '20px', pr: '14px', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: `1px solid ${C.bdr}`, flexShrink: 0 }}>
          <Box component="span" aria-hidden sx={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            bgcolor: C.purpBg, color: C.purple, fontSize: 15,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            ⬡
          </Box>
          <Typography component="h2" sx={{ m: 0, flex: 1, fontFamily: FONT_TIT, fontSize: folha ? 16 : 15, fontWeight: 700, color: C.text }}>
            {emLote ? 'Envio em massa' : 'Templates aprovados'}
          </Typography>
          <Box component="button" type="button" onClick={fecharPainelTemplates} aria-label="Fechar templates"
            sx={{
              width: folha ? 36 : 32, height: folha ? 36 : 32, p: 0, border: 0, borderRadius: '50%', cursor: 'pointer',
              bgcolor: C.inset, color: C.text2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '&:hover': { color: C.text },
              '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
            }}>
            <FiX size={folha ? 16 : 15} strokeWidth={2.4} />
          </Box>
        </Box>

        {/* Quem vai receber (em massa) */}
        {emLote && (
          <Box sx={{
            m: '16px 16px 0', p: '12px 14px', borderRadius: '14px',
            bgcolor: C.greenBg, border: `1px solid ${C.greenB}`, flexShrink: 0,
          }}>
            <Typography sx={{
              fontFamily: FONT_TXT, fontSize: 11, fontWeight: 700,
              letterSpacing: '0.1em', textTransform: 'uppercase', color: C.greenD, mb: '4px',
            }}>
              Conversas selecionadas
            </Typography>
            <Typography sx={{ fontFamily: FONT_TIT, fontSize: 26, fontWeight: 700, color: C.greenD, lineHeight: 1 }}>
              {selectedConvIds.size}
            </Typography>
            <Typography sx={{
              fontFamily: FONT_TXT, fontSize: 12.5, color: C.greenD, opacity: 0.8, mt: '4px',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {selectedNames}
            </Typography>
          </Box>
        )}

        {emLote && (
          <Typography sx={{
            fontFamily: FONT_TXT, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: C.cap, p: '18px 20px 10px', flexShrink: 0,
          }}>
            Escolha o template
          </Typography>
        )}

        {/* Lista */}
        <Box sx={{
          flex: '1 1 auto', minHeight: 0, overflowY: 'auto',
          p: emLote ? '0 16px 16px' : (folha ? '6px 16px calc(20px + env(safe-area-inset-bottom))' : '16px'),
          display: 'flex', flexDirection: 'column', gap: '8px',
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { background: C.bdr2, borderRadius: 3 },
        }}>
          {loadingTemplates ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} sx={{ color: C.purple }} />
            </Box>
          ) : templatesError ? (
            caixaAviso('erro', templatesError)
          ) : templates.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 3, fontSize: 14, color: C.cap, fontFamily: FONT_TXT }}>
              Nenhum template aprovado.
            </Typography>
          ) : templates.map(tpl => {
            const isSel = pickedTemplateName === tpl.name;
            const Icone = tpl.has_media ? FiImage : tpl.category === 'UTILITY' ? FiMail : FiFileText;
            return (
              <Box key={tpl.name} component="button" type="button"
                // Em massa: marca o template. Avulso: envia direto.
                onClick={() => (emLote ? setPickedTemplateName(isSel ? null : tpl.name) : handleSendTemplateSingle(tpl))}
                aria-pressed={emLote ? isSel : undefined}
                aria-label={emLote ? undefined : `Enviar o template ${tpl.label || tpl.name}`}
                sx={{
                  width: '100%', textAlign: 'left', font: 'inherit', flexShrink: 0,
                  bgcolor: isSel ? C.purpBg : C.surf,
                  border: `1px solid ${isSel ? C.purple : C.bdr}`,
                  borderRadius: '14px', p: '12px 14px', cursor: 'pointer', color: C.text,
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': { borderColor: C.purpB, bgcolor: C.purpBg },
                  '&:focus-visible': { outline: `2px solid ${C.purple}`, outlineOffset: 2 },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '6px' }}>
                  <Box component="span" aria-hidden sx={{
                    width: 30, height: 30, borderRadius: '9px', flexShrink: 0,
                    bgcolor: C.purpBg, border: `1px solid ${C.purpB}`, color: C.purpD,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icone size={14} />
                  </Box>
                  <Typography component="span" sx={{
                    fontFamily: FONT_TXT, fontSize: 14, fontWeight: 600,
                    color: C.text, flex: 1, minWidth: 0, overflowWrap: 'anywhere',
                  }}>
                    {tpl.label || tpl.name}
                  </Typography>
                  {emLote && (
                    <Box component="span" aria-hidden sx={{
                      width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                      border: `1.5px solid ${isSel ? C.purple : C.purpB}`,
                      bgcolor: isSel ? C.purple : 'transparent', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isSel && <FiCheck size={11} strokeWidth={3.2} />}
                    </Box>
                  )}
                </Box>
                <Typography component="span" sx={{
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  fontFamily: FONT_TXT, fontSize: 13, color: C.text2, lineHeight: 1.45, mb: '10px',
                }}>
                  {tpl.preview || 'Sem prévia disponível.'}
                </Typography>
                <Box component="span" sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {tpl.category && (
                    <Box component="span" sx={{
                      height: 22, px: '9px', display: 'inline-flex', alignItems: 'center',
                      fontSize: 11.5, color: C.text2, bgcolor: C.inset, borderRadius: 999,
                      border: `1px solid ${C.bdr}`, fontFamily: FONT_TXT,
                    }}>
                      {CATEGORIA_TEMPLATE[tpl.category] || tpl.category}
                    </Box>
                  )}
                  <Box component="span" sx={{
                    height: 22, px: '9px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                    fontSize: 11.5, fontWeight: 600, borderRadius: 999, bgcolor: C.greenBg, color: C.greenD,
                    border: `1px solid ${C.greenB}`, fontFamily: FONT_TXT,
                  }}>
                    <FiCheck size={11} strokeWidth={3} aria-hidden /> Aprovado
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Enviar (só em massa) */}
        {emLote && (
          <Box sx={{
            p: folha ? '12px 16px calc(16px + env(safe-area-inset-bottom))' : '12px 16px 16px',
            borderTop: `1px solid ${C.bdr}`, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            {bulkResult && caixaAviso(
              bulkResult.failed === 0 ? 'ok' : 'aviso',
              bulkResult.failed === 0
                ? `Template enviado para ${bulkResult.sent} conversa${bulkResult.sent !== 1 ? 's' : ''}.`
                : bulkResult.motivo
                  ? `${bulkResult.sent ? `${bulkResult.sent} enviado(s). ` : ''}${bulkResult.motivo}`
                  : `${bulkResult.sent} enviado(s), ${bulkResult.failed} falharam.`,
            )}
            {pickedTemplateName && bulkPreflight && !bulkResult && (
              <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                <ProtecaoDisparo
                  preflight={bulkPreflight} total={selectedConvIds.size}
                  optin={bulkOptin} onOptin={setBulkOptin}
                  cor={C.text} corSuave={C.text3}
                />
              </Box>
            )}
            <Box
              component="button" type="button"
              onClick={handleSendTemplateBulk}
              disabled={!podeEnviarLote}
              sx={{
                width: '100%', height: folha ? 50 : 44, px: '16px',
                bgcolor: podeEnviarLote ? C.purple : C.inset,
                color: podeEnviarLote ? '#fff' : C.cap,
                border: 'none', borderRadius: 999,
                fontFamily: FONT_TXT, fontSize: folha ? 15.5 : 14.5, fontWeight: 600,
                cursor: podeEnviarLote ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                boxShadow: podeEnviarLote ? '0 10px 24px -12px rgba(124,58,237,0.7)' : 'none',
                transition: 'background-color 0.15s, filter 0.15s',
                '&:hover:not(:disabled)': { filter: 'brightness(1.08)' },
                '&:focus-visible': { outline: `2px solid ${C.purple}`, outlineOffset: 3 },
              }}
            >
              {bulkSending ? (
                <><CircularProgress size={14} sx={{ color: '#fff' }} /> Enviando…</>
              ) : bulkBloqueio === 'tema' ? (
                <>Template não permitido</>
              ) : bulkBloqueio === 'conta' ? (
                <>Disparos parados pela Meta</>
              ) : bulkBloqueio === 'teste' ? (
                <>Limite do teste atingido</>
              ) : (
                <><Box component="span" aria-hidden>⬡</Box> Enviar para {selectedConvIds.size} contato{selectedConvIds.size !== 1 ? 's' : ''}</>
              )}
            </Box>
          </Box>
        )}
      </>
    );
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────
  const selectedChipPalette = selectedClient ? avatarPalette(selectedClient.name) : null;

  return (
    <ThemeProvider theme={TEMA_FONTES}>
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: C.bg, fontFamily: FONT_TXT, color: C.text, overflow: 'hidden' }}>
      <AppSidebar user={user} />

      <Box component="main" sx={{
        flexGrow: 1,
        ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
        transition: 'margin-left 0.25s ease',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* TopBar reaproveitada do dashboard (dark) — no mobile, quando um
            contato está aberto, o botão da esquerda vira "voltar" pra lista
            (comportamento igual ao WhatsApp mobile). Em desktop, essa lógica
            não muda nada — onBack só é honrado quando isMobile é true. */}
        <TopBar
          crumbBase="Conversas"
          crumbCur={
            isMobile && selectedClient
              ? (selectedClient.name || selectedClient.phone_number)
              : (device?.name || 'Chip')
          }
          canAddChip={false}
          onAddChip={() => {}}
          onNewConversation={() => setOpenNewConvModal(true)}
          onBack={isMobile && selectedClient ? () => setSelectedClient(null) : undefined}
          themeMode={themeMode}
          onToggleTheme={toggleTheme}
        />

        {/* ═══ 3-COL LAYOUT (desktop) / MASTER-DETAIL (mobile) ═══
            Em mobile: se `selectedClient` está null, só a lista. Se está
            preenchido, só o chat (com botão voltar na TopBar). O panel
            direito de templates fica escondido — envio pontual é feito
            direto pela tela do chat. */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* ──────────── COL ESQUERDA: Lista de conversas ──────────── */}
          <Box sx={{
            width: { xs: '100%', md: 360 },
            minWidth: { xs: 0, md: 360 },
            bgcolor: C.card,
            borderRight: { xs: 'none', md: `1px solid ${C.bdr}` },
            display: { xs: selectedClient ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Chip: nome, número e saúde. O ponto é a conexão em tempo real
                (verde = WebSocket ligado; âmbar = caiu pro polling). */}
            <Box sx={{ p: '16px 20px 14px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  title={wsConnected ? 'Conectado em tempo real' : 'Reconectando — atualizando a cada 10 s'}
                  sx={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    bgcolor: wsConnected ? C.green : C.amber,
                    boxShadow: `0 0 0 3px ${wsConnected ? C.greenBg : C.amberBg}`,
                  }} />
                <Typography sx={{ fontFamily: FONT_TIT, fontSize: 15, fontWeight: 700, color: C.text, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {device?.name || 'Carregando…'}
                </Typography>
                {device?.phone_number_id && (
                  <Tooltip title="Número oficial da Meta">
                    <Box component="span" sx={{ display: 'flex', color: C.green }}><FiCheckCircle size={14} /></Box>
                  </Tooltip>
                )}
              </Box>
              <Typography sx={{ fontFamily: FONT_NUM, fontSize: 12.5, color: C.cap, fontVariantNumeric: 'tabular-nums' }}>
                {device?.phone_number ? `${formatarFone(device.phone_number)} · ` : ''}Chip oficial Meta
              </Typography>
              {typeof device?.health_score === 'number' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '10px' }}>
                  <Typography sx={{ fontFamily: FONT_TXT, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.cap }}>
                    Saúde
                  </Typography>
                  <Box sx={{ flex: 1, height: 4, borderRadius: 999, bgcolor: C.inset, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${device.health_score}%`, borderRadius: 999, bgcolor: healthColor(device.health_score) }} />
                  </Box>
                  <Typography sx={{ fontFamily: FONT_NUM, fontSize: 12.5, fontWeight: 700, color: healthColor(device.health_score), fontVariantNumeric: 'tabular-nums' }}>
                    {device.health_score}%
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Busca */}
            <Box sx={{ px: 2 }}>
              <Box sx={{
                height: 40, display: 'flex', alignItems: 'center', gap: 1, px: 1.5,
                bgcolor: C.inset, border: '1px solid transparent', borderRadius: '12px',
                transition: 'border-color 0.2s, background-color 0.2s',
                '&:focus-within': { borderColor: C.greenB, bgcolor: C.card },
              }}>
                <FiSearch size={16} color="var(--ch-cap)" aria-hidden />
                <Box component="input" type="search"
                  aria-label="Buscar contato ou número"
                  placeholder="Buscar contato ou número"
                  value={searchClients}
                  onChange={e => setSearchClients(e.target.value)}
                  sx={{
                    flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none',
                    fontFamily: FONT_TXT, fontSize: 14, color: C.text,
                    '&::placeholder': { color: C.cap, opacity: 1 },
                    '&::-webkit-search-cancel-button': { filter: 'grayscale(1)', cursor: 'pointer' },
                  }}
                />
              </Box>
            </Box>

            {/* Barra de lote — só com ≥1 conversa selecionada */}
            {selectedConvIds.size > 0 && (
              <Box sx={{
                m: '12px 12px 0', p: '8px 8px 8px 14px', borderRadius: '14px',
                bgcolor: C.greenBg, border: `1px solid ${C.greenB}`,
                display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap',
                animation: 'slideIn 0.2s ease',
                '@keyframes slideIn': {
                  from: { opacity: 0, transform: 'translateY(-6px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}>
                <Typography role="status" sx={{ fontFamily: FONT_TXT, fontSize: 13, fontWeight: 700, color: C.greenD, flex: 1, minWidth: 110 }}>
                  {selectedConvIds.size} selecionada{selectedConvIds.size !== 1 ? 's' : ''}
                </Typography>
                <BulkBtn onClick={() => setShowTmplPanel(true)} sx={{ bgcolor: C.purple, color: '#fff' }}>
                  <Box component="span" aria-hidden sx={{ fontSize: 13 }}>⬡</Box> Enviar template
                </BulkBtn>
                <BulkBtn onClick={() => setBulkDeleteConfirm(true)} sx={{ bgcolor: C.redBg, color: C.redD, border: `1px solid ${C.redB}` }}>
                  <FiTrash2 size={12} aria-hidden /> Excluir
                </BulkBtn>
                <BulkBtn onClick={clearSelection} sx={{ bgcolor: 'transparent', color: C.text2, border: `1px solid ${C.bdr2}` }}>
                  <FiX size={12} aria-hidden /> Limpar
                </BulkBtn>
              </Box>
            )}

            {/* Filtros — Todas / Não lidas / Novos / Etiquetas ▾ / Pastas ▾ /
                Interesse ▾. Etiquetas, Pastas e Interesse abrem dropdowns MUI
                ancorados no próprio chip. Só UMA opção ativa por vez
                (clicar em "Todas" limpa a anterior).

                A faixa fica em UMA linha, rolando na horizontal — decisão de
                produto. Os seis chips têm ~510 px numa coluna de 360, então
                os últimos nascem fora da vista: até out/26 a pista era um
                degradê fixo na borda direita, que continuava lá depois de
                chegar ao fim e não deixava voltar. A `FaixaRolavel` mostra
                degradê e seta < > só no lado que ainda tem filtro (redesign
                "Chat Cetto.dc.html", 1b). */}
            {(() => {
              const isAll = filterTab === 'all';
              const isUnread = filterTab === 'unread';
              const isNewTab = filterTab === 'new';
              const activeLabel =
                (typeof filterTab === 'object' && filterTab?.type === 'label')
                  ? filterTab : null;
              const activeFolder =
                (typeof filterTab === 'object' && filterTab?.type === 'folder')
                  ? filterTab : null;
              const activeInterest =
                (typeof filterTab === 'object' && filterTab?.type === 'interest')
                  ? filterTab : null;

              const chipBase = (active) => ({
                height: 32, px: '12px', borderRadius: 999, flexShrink: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                fontFamily: FONT_TXT, fontSize: 13, fontWeight: active ? 600 : 500,
                border: `1px solid ${active ? C.greenB : C.bdr2}`,
                bgcolor: active ? C.greenBg : 'transparent',
                color: active ? C.greenD : C.text2,
                transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                '&:hover': active ? {} : { color: C.text, borderColor: C.cap },
                '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
              });
              const contador = (n, active) => (
                <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: 12, fontVariantNumeric: 'tabular-nums', color: active ? C.greenD : C.cap }}>
                  {n}
                </Box>
              );
              const seta = <FiChevronDown size={13} style={{ opacity: 0.7 }} aria-hidden />;

              return (
                <FaixaRolavel fundo={C.card}>
                  <Box component="button" type="button" aria-pressed={isAll} onClick={() => setFilterTab('all')} sx={chipBase(isAll)}>
                    Todas
                    {counts.all > 0 && contador(counts.all, isAll)}
                  </Box>
                  <Box component="button" type="button" aria-pressed={isUnread} onClick={() => setFilterTab('unread')} sx={chipBase(isUnread)}>
                    Não lidas
                    {counts.unread > 0 && contador(counts.unread, isUnread)}
                  </Box>
                  {/* Novos — contatos criados há < 3 dias OU sem outbound */}
                  <Box component="button" type="button" aria-pressed={isNewTab} onClick={() => setFilterTab('new')} sx={chipBase(isNewTab)}>
                    <IconNew size={12} />
                    Novos
                    {counts.new > 0 && contador(counts.new, isNewTab)}
                  </Box>
                  <Box component="button" type="button" aria-haspopup="menu"
                    onClick={(e) => setLabelMenuAnchor(e.currentTarget)} sx={chipBase(!!activeLabel)}>
                    {activeLabel ? `Etiqueta: ${activeLabel.name}` : 'Etiquetas'}
                    {seta}
                  </Box>
                  {/* Pastas ▾ — as pastas do CRM, de verdade. */}
                  <Box component="button" type="button" aria-haspopup="menu"
                    onClick={(e) => setFolderMenuAnchor(e.currentTarget)} sx={chipBase(!!activeFolder)}>
                    {activeFolder ? `Pasta: ${activeFolder.name}` : 'Pastas'}
                    {seta}
                  </Box>
                  {/* Interesse ▾ — o nível do CRM, que até out/26 morava
                      (mal rotulado) dentro do chip "Pastas". */}
                  <Box component="button" type="button" aria-haspopup="menu"
                    onClick={(e) => setInterestMenuAnchor(e.currentTarget)} sx={chipBase(!!activeInterest)}>
                    {activeInterest ? `Interesse: ${INTEREST_LABEL[activeInterest.value]}` : 'Interesse'}
                    {seta}
                  </Box>
                </FaixaRolavel>
              );
            })()}

            {/* Dropdown das ETIQUETAS — lista de labels da company. */}
            <Menu
              anchorEl={labelMenuAnchor}
              open={Boolean(labelMenuAnchor)}
              onClose={() => setLabelMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 0.5, borderRadius: '10px', minWidth: 220, maxWidth: 280, maxHeight: 340 } }}
            >
              <Box sx={{
                px: 1.5, py: 1, borderBottom: `1px solid ${C.bdr}`,
                fontFamily: FONT_TXT, fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase', color: C.text3,
              }}>
                Filtrar por etiqueta
              </Box>
              {allLabels.length === 0 ? (
                <Box sx={{ px: 1.5, py: 1.5, fontFamily: FONT_TXT, fontSize: '0.72rem', color: C.text3 }}>
                  Nenhuma etiqueta criada ainda.
                </Box>
              ) : allLabels.map(l => {
                const count = counts.byLabel[l.id] || 0;
                const active = typeof filterTab === 'object' && filterTab?.type === 'label' && filterTab.id === l.id;
                return (
                  <MenuItem
                    key={l.id}
                    onClick={() => {
                      setFilterTab({ type: 'label', id: l.id, name: l.name });
                      setLabelMenuAnchor(null);
                    }}
                    sx={{ py: 0.7, fontFamily: FONT_TXT, fontSize: '0.75rem' }}
                  >
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: LABEL_COLOR_MAP[l.color]?.dot || LABEL_COLOR_MAP.green.dot,
                      mr: 1,
                    }} />
                    <Box sx={{ flex: 1, fontWeight: active ? 700 : 500 }}>{l.name}</Box>
                    {count > 0 && (
                      <Box sx={{
                        fontFamily: FONT_NUM, fontSize: '0.62rem',
                        bgcolor: C.inset, px: 0.7, py: 0.05, borderRadius: '20px',
                        color: C.text3,
                      }}>
                        {count}
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Dropdown das PASTAS — as do CRM, vindas de GET /folders. */}
            <Menu
              anchorEl={folderMenuAnchor}
              open={Boolean(folderMenuAnchor)}
              onClose={() => setFolderMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 0.5, borderRadius: '10px', minWidth: 220, maxHeight: 340 } }}
            >
              <Box sx={{
                px: 1.5, py: 1, borderBottom: `1px solid ${C.bdr}`,
                fontFamily: FONT_TXT, fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase', color: C.text3,
              }}>
                Filtrar por pasta
              </Box>
              {allFolders.length === 0 ? (
                <Box sx={{
                  px: 1.5, py: 1.5, fontFamily: FONT_TXT,
                  fontSize: '0.72rem', color: C.text3, maxWidth: 240,
                }}>
                  Nenhuma pasta criada ainda. Crie em Contatos → Pasta para
                  organizar seus leads por aqui.
                </Box>
              ) : allFolders.map(f => {
                const count = counts.byFolder[f.id] || 0;
                const active = typeof filterTab === 'object'
                  && filterTab?.type === 'folder' && filterTab.id === f.id;
                return (
                  <MenuItem
                    key={f.id}
                    onClick={() => {
                      setFilterTab({ type: 'folder', id: f.id, name: f.name });
                      setFolderMenuAnchor(null);
                    }}
                    sx={{ py: 0.7, fontFamily: FONT_TXT, fontSize: '0.75rem' }}
                  >
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '2px',
                      bgcolor: FOLDER_DOT[f.color] || FOLDER_DOT.blue, mr: 1,
                    }} />
                    <Box sx={{
                      flex: 1, fontWeight: active ? 700 : 500,
                      overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {f.name}
                    </Box>
                    {count > 0 && (
                      <Box sx={{
                        fontFamily: FONT_NUM, fontSize: '0.62rem',
                        bgcolor: C.inset, px: 0.7, py: 0.05, borderRadius: '20px',
                        color: C.text3, ml: 0.8,
                      }}>
                        {count}
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Dropdown do INTERESSE (status CRM). */}
            <Menu
              anchorEl={interestMenuAnchor}
              open={Boolean(interestMenuAnchor)}
              onClose={() => setInterestMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 0.5, borderRadius: '10px', minWidth: 200 } }}
            >
              <Box sx={{
                px: 1.5, py: 1, borderBottom: `1px solid ${C.bdr}`,
                fontFamily: FONT_TXT, fontSize: '0.62rem', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase', color: C.text3,
              }}>
                Filtrar por interesse
              </Box>
              {INTEREST_ORDER.map(key => {
                const count = counts.byInterest[key] || 0;
                const active = typeof filterTab === 'object' && filterTab?.type === 'interest' && filterTab.value === key;
                return (
                  <MenuItem
                    key={key}
                    onClick={() => {
                      setFilterTab({ type: 'interest', value: key });
                      setInterestMenuAnchor(null);
                    }}
                    sx={{ py: 0.7, fontFamily: FONT_TXT, fontSize: '0.75rem' }}
                  >
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: INTEREST_DOT[key], mr: 1,
                    }} />
                    <Box sx={{ flex: 1, fontWeight: active ? 700 : 500 }}>{INTEREST_LABEL[key]}</Box>
                    {count > 0 && (
                      <Box sx={{
                        fontFamily: FONT_NUM, fontSize: '0.62rem',
                        bgcolor: C.inset, px: 0.7, py: 0.05, borderRadius: '20px',
                        color: C.text3,
                      }}>
                        {count}
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Selecionar todas. No celular só aparece no modo de seleção
                (que começa segurando o dedo numa conversa). */}
            {(!isMobile || selectedConvIds.size > 0) && (
              <Box sx={{ p: '2px 20px 8px 18px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CheckBox state={selectAllState} onClick={toggleSelectAll} rotulo="Selecionar todas as conversas" />
                <Typography sx={{ fontFamily: FONT_TXT, fontSize: 12.5, fontWeight: 500, color: C.cap, flex: 1 }}>
                  Selecionar conversas
                </Typography>
                <Typography sx={{ fontFamily: FONT_NUM, fontSize: 12.5, color: C.cap, fontVariantNumeric: 'tabular-nums' }}>
                  {selectedConvIds.size} / {filteredClients.length}
                </Typography>
              </Box>
            )}

            {/* Lista */}
            <Box sx={{
              flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px', pb: 1.5,
              '&::-webkit-scrollbar': { width: 3 },
              '&::-webkit-scrollbar-thumb': { background: C.bdr2, borderRadius: 3 },
            }}>
              {/* Recomendação de limpeza (out/26).
                  Fica no TOPO da lista: na primeira versão ficou junto do
                  "Ver mais", lá embaixo — depois de 500 conversas, ninguém
                  rolaria até o aviso que pede pra encurtar a lista.
                  Aparece ao passar do limiar que o backend define. NÃO
                  bloqueia nada: conversa nova sempre entra, porque recusar
                  significaria perder a mensagem de um cliente final. É só
                  um convite a arrumar a casa, com o botão que arruma. */}
              {totalConversas != null && totalConversas >= avisoConversas && (
                <Box sx={{
                  m: '4px 12px 6px', p: '12px 14px', borderRadius: '14px', flexShrink: 0,
                  bgcolor: C.amberBg, border: `1px solid ${C.amberB}`,
                }}>
                  <Typography sx={{ fontFamily: FONT_TXT, fontSize: 13, fontWeight: 700, color: C.amberD, mb: 0.3 }}>
                    {totalConversas.toLocaleString('pt-BR')} conversas neste chip
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_TXT, fontSize: 12.5, color: C.amberD, lineHeight: 1.5, mb: 1 }}>
                    O chat carrega as {limiteCarga.toLocaleString('pt-BR')} mais recentes.
                    Limpar as antigas deixa a lista mais leve — os contatos
                    continuam em Contatos, só a conversa sai do chat.
                  </Typography>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setLimpezaAberta(true)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.6,
                      px: 1.4, py: 0.7, borderRadius: 999, cursor: 'pointer',
                      bgcolor: C.amber, color: C.sobreVerde, border: 'none',
                      fontFamily: FONT_TXT, fontSize: 12.5, fontWeight: 700,
                      '&:hover': { filter: 'brightness(1.08)' },
                    }}
                  >
                    <FiTrash2 size={12} aria-hidden />
                    Limpar conversas antigas
                  </Box>
                </Box>
              )}

              {loadingClients ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} sx={{ color: C.green }} />
                </Box>
              ) : filteredClients.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <FiMessageSquare size={28} color="var(--ch-cap)" />
                  <Typography sx={{ color: C.cap, mt: 1, fontSize: 14, fontFamily: FONT_TXT }}>
                    {searchClients ? 'Nenhuma conversa com esse nome ou número.' : 'Nenhuma conversa neste filtro.'}
                  </Typography>
                </Box>
              ) : visibleClients.map(client => {
                const isSelected = selectedClient?.id === client.id;
                const isChecked = selectedConvIds.has(client.id);
                const hasUnread = client.unread > 0 && !isSelected;
                const preview = getLastMessagePreview(client);
                const avPal = avatarPalette(client.name);
                const initials = (client.name || '?')[0]?.toUpperCase();
                const lastTime = formatLastMsgTime(client?.last_message?.timestamp);
                const modoSelecao = selectedConvIds.size > 0;
                // No celular o círculo só aparece no modo de seleção; no
                // computador fica sempre (é o jeito de selecionar).
                const comCirculo = !isMobile || modoSelecao;

                const convLabels = client.labels || [];
                const popoverOpen = labelPopoverClientId === client.id && !etiquetaNoPainel;
                const comEtiquetas = client.isNew || convLabels.length > 0 || isSelected || popoverOpen;
                const PreviewIcone = preview.Icone;
                return (
                  <Box key={client.id}
                    onClick={() => {
                      if (toqueLongoRef.current.disparou) { toqueLongoRef.current.disparou = false; return; }
                      if (isMobile && modoSelecao) { toggleConvSelected(client.id); return; }
                      handleSelectClient(client);
                    }}
                    // Toque longo (celular): entra no modo de seleção marcando
                    // esta conversa, como no WhatsApp. Rolar a lista cancela.
                    // A marca "disparou" engole o clique que o navegador pode
                    // mandar ao soltar o dedo; um toque NOVO a zera — senão,
                    // quando o navegador não manda esse clique, o toque
                    // seguinte (marcar outra conversa) era engolido no lugar.
                    onTouchStart={isMobile ? () => {
                      clearTimeout(toqueLongoRef.current.timer);
                      toqueLongoRef.current.disparou = false;
                      toqueLongoRef.current.timer = setTimeout(() => {
                        toqueLongoRef.current.disparou = true;
                        toggleConvSelected(client.id);
                        try { navigator.vibrate?.(12); } catch { /* sem vibração */ }
                      }, 450);
                    } : undefined}
                    onTouchMove={isMobile ? () => clearTimeout(toqueLongoRef.current.timer) : undefined}
                    onTouchEnd={isMobile ? () => clearTimeout(toqueLongoRef.current.timer) : undefined}
                    onTouchCancel={isMobile ? () => clearTimeout(toqueLongoRef.current.timer) : undefined}
                    onContextMenu={isMobile ? (e) => e.preventDefault() : undefined}
                    sx={{
                      m: '0 8px', p: '10px', borderRadius: '14px',
                      display: 'flex', flexDirection: 'column', gap: '6px',
                      // A lista é coluna flex: sem isto, com `overflow:
                      // hidden`, a linha encolhe até sumir quando há muitas.
                      flexShrink: 0,
                      cursor: 'pointer', position: 'relative',
                      bgcolor: (isSelected || isChecked) ? C.sel : 'transparent',
                      transition: 'background-color 0.15s',
                      // Quando o popover está aberto neste item, ele aparece
                      // pra fora da linha.
                      overflow: popoverOpen ? 'visible' : 'hidden',
                      ...(isMobile ? { WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' } : {}),
                      '&:hover': { bgcolor: (isSelected || isChecked) ? C.sel : C.hover },
                      '&:hover .pin-btn': { opacity: 1 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {comCirculo && (
                        <CheckBox
                          state={isChecked ? 'all' : 'none'}
                          rotulo={`Selecionar ${client.name || client.phone_number}`}
                          onClick={(e) => { e.stopPropagation(); toggleConvSelected(client.id); }}
                        />
                      )}

                      <Box sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        bgcolor: avPal.bg, color: avPal.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: FONT_TIT, fontSize: 16, fontWeight: 700, flexShrink: 0,
                      }}>
                        {initials}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                          <Typography sx={{
                            flex: 1, minWidth: 0, fontFamily: FONT_TIT, fontSize: 14.5, fontWeight: 700, color: C.text,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {client.name || formatarFone(client.phone_number)}
                          </Typography>
                          <Typography sx={{
                            fontFamily: FONT_NUM, fontSize: 12, flexShrink: 0, fontVariantNumeric: 'tabular-nums',
                            color: hasUnread ? C.greenD : C.cap, fontWeight: hasUnread ? 600 : 400,
                          }}>
                            {lastTime}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 20 }}>
                          <Typography component="div" sx={{
                            flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '5px',
                            fontFamily: FONT_TXT, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden',
                            color: preview.isTemplate ? C.purpD : (hasUnread ? C.text : C.text2),
                            fontWeight: hasUnread ? 500 : 400,
                          }}>
                            {preview.isTemplate && <Box component="span" aria-hidden sx={{ flexShrink: 0 }}>⬡</Box>}
                            {PreviewIcone && <PreviewIcone size={13} style={{ flexShrink: 0 }} aria-hidden />}
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {preview.label}
                            </Box>
                          </Typography>
                          {/* Pin: fixada = sempre à vista; senão só no hover
                              da linha (no celular, só a fixada aparece). */}
                          <Tooltip title={client.is_pinned ? 'Desafixar conversa' : 'Fixar no topo'} placement="left">
                            <Box
                              component="button" type="button"
                              className="pin-btn"
                              aria-label={client.is_pinned ? 'Desafixar conversa' : 'Fixar conversa no topo'}
                              onClick={(e) => { e.stopPropagation(); togglePin(client); }}
                              sx={{
                                display: (isMobile && !client.is_pinned) ? 'none' : 'flex',
                                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                width: 22, height: 22, p: 0, border: 0, borderRadius: '6px', cursor: 'pointer',
                                bgcolor: 'transparent',
                                color: client.is_pinned ? C.cap : C.text3,
                                opacity: client.is_pinned ? 1 : 0,
                                transition: 'opacity .15s, background-color .15s',
                                '&:hover': { bgcolor: C.inset, color: C.text },
                                '&:focus-visible': { opacity: 1, outline: `2px solid ${C.green}` },
                              }}
                            >
                              {client.is_pinned ? <BsPinAngleFill size={12} /> : <BsPinAngle size={12} />}
                            </Box>
                          </Tooltip>
                          {hasUnread && (
                            <Box component="span" aria-label={`${client.unread} não lida${client.unread !== 1 ? 's' : ''}`} sx={{
                              minWidth: 20, height: 20, px: '6px', borderRadius: 999, flexShrink: 0,
                              bgcolor: C.green, color: C.sobreVerde,
                              fontFamily: FONT_NUM, fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {client.unread > 99 ? '99+' : client.unread}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Etiquetas, embaixo, alinhadas com o nome. O "+ etiqueta"
                        aparece só na conversa aberta — nas outras, poluía a
                        lista inteira com a mesma pílula tracejada. */}
                    {comEtiquetas && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap', pl: comCirculo ? '86px' : '56px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Chip virtual "Novo" — derivado (created<3d OU sem
                            outbound). Some sozinho quando as condições saem. */}
                        {client.isNew && (
                          <Box component="span" sx={{
                            height: 20, display: 'inline-flex', alignItems: 'center', gap: '4px', px: '8px',
                            borderRadius: 999, fontFamily: FONT_TXT, fontSize: 11, fontWeight: 600,
                            letterSpacing: '0.04em', textTransform: 'uppercase',
                            bgcolor: C.greenBg, color: C.greenD, border: `1px solid ${C.greenB}`,
                          }}>
                            <IconNew size={10} />
                            Novo
                          </Box>
                        )}
                        {convLabels.map(lbl => (
                          <LabelPill
                            key={lbl.id}
                            label={lbl}
                            onClick={() => toggleLabelOnClient(client.id, lbl.id)}
                          />
                        ))}
                        {(isSelected || popoverOpen) && (
                          <AddLabelChip
                            onClick={() => {
                              setEtiquetaNoPainel(false);
                              setLabelPopoverClientId(popoverOpen ? null : client.id);
                              setLabelSearch('');
                              setCreatingLabel(false);
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {/* POPOVER de etiquetas — só renderiza pro item ativo */}
                    {popoverOpen && (
                      <LabelPopover esquerda={comCirculo ? 86 : 56} {...propsPopoverEtiquetas(client)} />
                    )}
                  </Box>
                );
              })}

              {/* Ver mais conversas — client-side pagination.
                  Cada clique soma +CONVERSATIONS_PAGE_STEP itens ao slice
                  renderizado. filteredClients já contém todas as conversas
                  do filtro atual em memória, então o "load-more" é
                  instantâneo (só re-render). */}
              {!loadingClients && filteredClients.length > 0 && conversationsHasMore && (
                <Box sx={{ p: '14px 16px 18px', textAlign: 'center' }}>
                  <Box
                    component="button" type="button"
                    onClick={() => setConversationsVisible(v => v + CONVERSATIONS_PAGE_STEP)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.6,
                      height: 34, px: 2, borderRadius: 999, cursor: 'pointer',
                      bgcolor: 'transparent', border: `1px solid ${C.bdr2}`,
                      fontFamily: FONT_TXT, fontSize: 13, fontWeight: 600, color: C.text2,
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: C.greenB, color: C.greenD, bgcolor: C.greenBg },
                      '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
                    }}
                  >
                    Ver mais {Math.min(CONVERSATIONS_PAGE_STEP, filteredClients.length - conversationsVisible)} conversas
                  </Box>
                  {/* Quantas existem ALÉM das que o servidor mandou. O "ver
                      mais" acima pagina o que já está em memória; isto aqui
                      é o resto, que só sai do caminho com limpeza. */}
                  {totalConversas > clients.length && (
                    <Typography sx={{ fontFamily: FONT_TXT, fontSize: 12, color: C.cap, mt: 0.8, lineHeight: 1.5 }}>
                      Mostrando as {clients.length.toLocaleString('pt-BR')} mais recentes
                      de {totalConversas.toLocaleString('pt-BR')}.
                    </Typography>
                  )}
                  <Typography sx={{ fontFamily: FONT_NUM, fontSize: 12, color: C.cap, mt: 0.8, fontVariantNumeric: 'tabular-nums' }}>
                    Mostrando {conversationsVisible} de {filteredClients.length}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* ──────────── COL CENTRAL: Chat ──────────── */}
          <Box sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: selectedClient ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: C.bg,
            width: { xs: '100%', md: 'auto' },
          }}>
            {selectedClient ? (() => {
              // A lista é a fonte de verdade de etiquetas, perfil e
              // qualificação: `selectedClient` é uma cópia do clique.
              const live = clienteAberto;
              const perfil = (live.whatsapp_name || '').trim();
              const perfilRepete = perfil && perfil.toLowerCase() === (live.name || '').trim().toLowerCase();
              const etiquetasDoTopo = live.labels || [];
              const inicial = (selectedClient.name || '?')[0]?.toUpperCase();
              const painelAberto = isMobile ? contatoFolha : (contatoAberto && !showTmplPanel);
              const abrirContato = () => {
                if (isMobile) { setContatoFolha(true); return; }
                if (showTmplPanel) {
                  // Templates e contato dividem a coluna da direita: pedir
                  // o contato fecha o painel de templates (como o design).
                  setShowTmplPanel(false); setPickedTemplateName(null); setBulkResult(null);
                  alternarContato(true);
                  return;
                }
                alternarContato();
              };

              // Botão redondo do topo (vídeo, contato): 36 px no
              // computador, 40 no celular — dedo, não cursor.
              const botaoTopoSx = (ativo = false) => ({
                width: { xs: 40, md: 36 }, height: { xs: 40, md: 36 }, p: 0, flexShrink: 0,
                borderRadius: '50%', cursor: 'pointer',
                border: `1px solid ${ativo ? C.greenB : 'transparent'}`,
                bgcolor: ativo ? C.greenBg : C.inset,
                color: ativo ? C.greenD : C.text2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'color 0.15s, background-color 0.15s, border-color 0.15s',
                '&:hover': { color: ativo ? C.greenD : C.text },
                '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
              });
              // Ferramenta da caixa de mensagem (templates, formulário,
              // emoji, anexo, IA): círculo sem borda, como no design.
              const ferramentaSx = (cor = C.text2, fundoHover = C.inset, ativo = false) => ({
                width: 36, height: 36, p: 0, flexShrink: 0, border: 0, borderRadius: '50%',
                cursor: 'pointer', font: 'inherit',
                bgcolor: ativo ? fundoHover : 'transparent', color: cor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.15s, color 0.15s',
                '&:hover': { bgcolor: fundoHover, color: cor === C.text2 ? C.text : cor },
                '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 1 },
                '&:disabled': { opacity: 0.4, cursor: 'not-allowed', bgcolor: 'transparent' },
              });
              const tamBotao = isMobile ? 44 : 36;
              const botaoVerdeSx = {
                width: tamBotao, height: tamBotao, p: 0, flexShrink: 0, border: 0, borderRadius: '50%',
                bgcolor: C.green, color: C.sobreVerde, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                '&:hover': { bgcolor: 'var(--ch-green-hover)' },
                '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
              };

              const campoTexto = (
                <Box component="textarea"
                  ref={inputRef}
                  aria-label="Mensagem"
                  placeholder="Digite uma mensagem…"
                  rows={1}
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault(); handleSendMessage();
                    }
                    if (e.key === 'Escape' && respondendoRef.current) {
                      e.preventDefault(); setRespondendo(null);
                    }
                  }}
                  sx={{
                    flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none',
                    fontFamily: FONT_TXT, fontSize: isMobile ? 16 : 14.5, color: C.text,
                    resize: 'none',
                    // 10 linhas × 20 px ≈ 200. Antes do teto o texto longo
                    // abria rolagem horizontal; o WhatsApp Web cresce até
                    // ~10 linhas antes de virar rolagem interna. No celular
                    // a fonte é 16 px — abaixo disso o iOS dá zoom no campo.
                    minHeight: 20, maxHeight: 200,
                    lineHeight: '20px',
                    overflowY: 'auto',
                    p: '8px',
                    '&::placeholder': { color: C.cap, opacity: 1 },
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: C.bdr2, borderRadius: 4 },
                  }}
                />
              );

              const botaoEnviar = (
                <Box component="button" type="button" onClick={() => handleSendMessage()}
                  aria-label="Enviar mensagem" title="Enviar" sx={botaoVerdeSx}>
                  <FiSend size={isMobile ? 18 : 16} style={{ marginLeft: -1 }} />
                </Box>
              );
              const botaoGravar = (
                <Box component="button" type="button" onClick={startRecording}
                  aria-label="Gravar áudio" title="Gravar áudio" sx={botaoVerdeSx}>
                  <FiMic size={isMobile ? 19 : 17} />
                </Box>
              );
              /* Gravando: descartar à ESQUERDA, enviar à direita, e o
                 contador no meio. A ordem importa — o verde fica onde o
                 botão de enviar sempre esteve, então quem estava com o
                 dedo no lugar de sempre não descarta o áudio por engano. */
              const botaoDescartar = (
                <Box component="button" type="button" onClick={cancelRecording}
                  aria-label="Descartar gravação" title="Descartar"
                  sx={{
                    width: tamBotao, height: tamBotao, p: 0, flexShrink: 0, borderRadius: '50%',
                    bgcolor: isMobile ? C.card : 'transparent', color: C.red,
                    border: `1px solid ${C.redB}`, cursor: 'pointer',
                    boxShadow: isMobile ? C.sh2 : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '&:hover': { bgcolor: C.redBg },
                    '&:focus-visible': { outline: `2px solid ${C.red}`, outlineOffset: 2 },
                  }}>
                  <FiTrash2 size={isMobile ? 18 : 15} />
                </Box>
              );
              const contadorGravacao = (
                <Box role="timer" aria-label={`Gravando áudio: ${formatarDuracao(segundosGravando)}`} sx={{
                  display: 'flex', alignItems: 'center', gap: '7px',
                  px: '12px', height: tamBotao, borderRadius: 999,
                  bgcolor: C.redBg, border: `1px solid ${C.redB}`, flexShrink: 0,
                  ...(isMobile ? { flex: 1, justifyContent: 'center' } : {}),
                }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%', bgcolor: C.red,
                    animation: 'pulsoGravacao 1.2s ease-in-out infinite',
                    '@keyframes pulsoGravacao': {
                      '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.25 },
                    },
                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                  }} />
                  <Typography component="span" sx={{
                    fontFamily: FONT_NUM, fontSize: 13.5, fontWeight: 700, color: C.redD,
                    minWidth: 34, fontVariantNumeric: 'tabular-nums',
                  }}>
                    {formatarDuracao(segundosGravando)}
                  </Typography>
                  {isMobile && (
                    <Typography component="span" sx={{ fontFamily: FONT_TXT, fontSize: 14, color: C.redD }}>
                      Gravando
                    </Typography>
                  )}
                </Box>
              );
              const botaoEnviarGravacao = (
                <Box component="button" type="button" onClick={stopRecording}
                  aria-label="Enviar gravação" title="Enviar" sx={botaoVerdeSx}>
                  <FiSend size={isMobile ? 18 : 16} style={{ marginLeft: -1 }} />
                </Box>
              );

              return (
              <>
                {/* Topo da conversa (out/26, redesign 1b). Vidro sobre o
                    fundo do chat; o "i" (anotações) e o "Abrir no CRM"
                    viraram UM botão de pessoa, que abre a mini-página do
                    contato. As etiquetas moram nela — aqui só aparecem
                    com ela fechada, numa linha com o número. */}
                <Box component="header" sx={{
                  height: { xs: 64, md: 68 }, flexShrink: 0, position: 'relative', zIndex: 3,
                  p: { xs: '0 10px 0 14px', md: '0 16px 0 20px' },
                  display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '12px' },
                  bgcolor: C.glass, backdropFilter: 'blur(20px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                  borderBottom: `1px solid ${C.bdr}`,
                }}>
                  <Box aria-hidden sx={{
                    width: { xs: 38, md: 40 }, height: { xs: 38, md: 40 }, borderRadius: '50%', flexShrink: 0,
                    bgcolor: selectedChipPalette.bg, color: selectedChipPalette.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: FONT_TIT, fontSize: { xs: 15, md: 16 }, fontWeight: 700,
                  }}>
                    {inicial}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: { xs: '2px', md: '3px' } }}>
                    <Typography component="h2" sx={{
                      m: 0, fontFamily: FONT_TIT, fontSize: { xs: 15.5, md: 16 }, fontWeight: 700, color: C.text,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25,
                    }}>
                      {selectedClient.name || formatarFone(selectedClient.phone_number)}
                    </Typography>
                    {/* Número + nome do perfil do WhatsApp (out/26), o
                        `contacts[].profile.name` da Meta. É o nome que a
                        pessoa escolheu, não o do CRM — por isso o "~", a
                        mesma marca que o WhatsApp usa pra quem não está
                        salvo. Some se for igual ao do CRM. */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden',
                      fontFamily: FONT_TXT, fontSize: 12.5, color: C.text2, whiteSpace: 'nowrap',
                    }}>
                      <Box component="span" sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                        {formatarFone(selectedClient.phone_number)}
                      </Box>
                      {perfil && !perfilRepete && (
                        <Box component="span" title="Nome no perfil do WhatsApp" sx={{
                          color: C.cap, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
                          display: { xs: 'none', sm: 'inline' },
                        }}>
                          ~{perfil}
                        </Box>
                      )}
                      {/* Etiquetas: clique remove, "+" abre o popover. Só
                          com a coluna da direita fechada (a conversa fica
                          estreita e elas eram cortadas) e no computador —
                          no celular moram na folha do contato. */}
                      {!isMobile && !painelAberto && !showTmplPanel && (
                        <>
                          <Box component="span" aria-hidden sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: C.text3, flexShrink: 0, mx: '2px' }} />
                          {etiquetasDoTopo.map(lbl => (
                            <Box key={lbl.id} component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
                              <LabelPill label={lbl} onClick={() => toggleLabelOnClient(selectedClient.id, lbl.id)} />
                            </Box>
                          ))}
                          <Tooltip title="Adicionar etiqueta">
                            <Box component="button" type="button"
                              aria-label="Adicionar etiqueta"
                              aria-expanded={popoverForaDaLista}
                              onClick={() => {
                                const abrir = !popoverForaDaLista;
                                setEtiquetaNoPainel(abrir);
                                setLabelPopoverClientId(abrir ? selectedClient.id : null);
                                setLabelSearch('');
                                setCreatingLabel(false);
                              }}
                              sx={{
                                width: 20, height: 20, p: 0, flexShrink: 0, borderRadius: '50%',
                                border: `1px dashed ${C.bdr2}`, bgcolor: 'transparent', color: C.cap,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                '&:hover': { color: C.text, borderColor: C.cap },
                                '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
                              }}>
                              <FiPlus size={11} strokeWidth={2.6} />
                            </Box>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                    {/* O popover das etiquetas aberto pelo "+" do topo (ou
                        da mini-página) ancora aqui, logo abaixo do nome. */}
                    {!isMobile && popoverForaDaLista && !painelAberto && !showTmplPanel && (
                      <Box sx={{ position: 'absolute', left: 72, top: 56, zIndex: 20 }}>
                        <LabelPopover {...propsPopoverEtiquetas(live)} esquerda={0} />
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '6px', md: '8px' }, flexShrink: 0 }}>
                    <QualificarNoChat
                      key={selectedClient.id}
                      cliente={live}
                      onMudou={aoQualificarChat}
                    />
                    <Tooltip title="Videochamada">
                      <Box component="button" type="button" aria-label="Iniciar videochamada"
                        onClick={() => handleStartCall('video')} sx={botaoTopoSx()}>
                        <FiVideo size={isMobile ? 17 : 16} />
                      </Box>
                    </Tooltip>
                    <Tooltip title={painelAberto ? 'Fechar contato' : 'Ver contato'}>
                      <Box component="button" type="button"
                        aria-label={painelAberto ? 'Fechar contato' : 'Ver contato'}
                        aria-pressed={painelAberto}
                        onClick={() => (painelAberto && !isMobile ? alternarContato(false) : abrirContato())}
                        sx={botaoTopoSx(painelAberto)}>
                        <FiUser size={isMobile ? 17 : 16} />
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Mensagens. Bolhas AGRUPADAS (out/26, redesign): seguidas
                    do mesmo lado ficam a 3 px e só a última tem o "rabinho"
                    (canto de 5 px) e o avatar; hora e risquinhos vão DENTRO
                    da bolha, no canto — antes ficavam numa linha embaixo de
                    cada uma e dobravam a altura da conversa. A nossa bolha
                    é verde translúcida (--ch-ob): o verde cheio com texto
                    branco cansava a leitura e, no escuro, ofuscava. */}
                <Box ref={messagesContainerRef} onScroll={handleMessagesScroll}
                  sx={{
                    flex: 1, overflowY: 'auto', overflowX: 'hidden',
                    p: { xs: '10px 14px 24px', md: '14px 28px 28px' },
                    display: 'flex', flexDirection: 'column',
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': { background: C.bdr2, borderRadius: 4 },
                  }}>
                  {messages.length === 0 ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                      <Typography sx={{ color: C.cap, fontSize: 14, fontFamily: FONT_TXT, textAlign: 'center' }}>
                        Inicie a conversa enviando uma mensagem ou template.
                      </Typography>
                    </Box>
                  ) : (<>
                    {/* "Ver mais" no topo — só com has_more. Cursor
                        (before_id), preservando a posição na tela. */}
                    {messagesHasMore && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <Box
                          component="button" type="button"
                          onClick={loadOlderMessages}
                          disabled={loadingOlderMessages}
                          sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            height: 32, px: '14px', borderRadius: 999, cursor: 'pointer',
                            bgcolor: C.card, border: `1px solid ${C.bdr}`, boxShadow: C.sh1,
                            fontFamily: FONT_TXT, fontSize: 13, fontWeight: 600, color: C.text2,
                            transition: 'border-color 0.15s, color 0.15s',
                            '&:hover:not(:disabled)': { borderColor: C.greenB, color: C.greenD },
                            '&:disabled': { cursor: 'default', opacity: 0.7 },
                            '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
                          }}
                        >
                          {loadingOlderMessages
                            ? <><CircularProgress size={12} sx={{ color: C.text2 }} /> Carregando…</>
                            : <><FiArrowUp size={13} aria-hidden /> Ver mais 20 mensagens</>}
                        </Box>
                      </Box>
                    )}
                    {messages.map((msg, i) => {
                    const isOut = msg.direction === 'OUTBOUND';
                    const isTmpl = (msg.message_type || '').toUpperCase() === 'TEMPLATE';
                    const ehFormulario = Boolean(msg.template_render?.formulario || msg.template_render?.formulario_resposta);
                    // Coexistência (set/26): esta bolha saiu do app do
                    // WhatsApp no CELULAR do dono, não daqui. Sem essa
                    // marca o agente vê uma resposta que ele não deu e não
                    // tem como saber se foi o dono ou a IA — as duas
                    // chegam sem agente associado, e é o `source` que
                    // separa. Mensagem anterior a set/26 vem sem `source`
                    // e cai no caminho normal.
                    const isFromPhone = (msg.source || '').toUpperCase() === 'MOBILE_APP';
                    const prev = messages[i - 1];
                    const next = messages[i + 1];
                    const dia = (m) => new Date(m.timestamp).toDateString();
                    // Template e formulário são cartões: nunca grudam.
                    const cartao = (m) => Boolean(m) && (
                      ((m.message_type || '').toUpperCase() === 'TEMPLATE' && m.direction === 'OUTBOUND')
                      || m.template_render?.formulario || m.template_render?.formulario_resposta);
                    const sameDayPrev = Boolean(prev) && dia(prev) === dia(msg);
                    const primeira = !prev || !sameDayPrev || prev.direction !== msg.direction || cartao(prev) || cartao(msg);
                    const ultima = !next || dia(next) !== dia(msg) || next.direction !== msg.direction || cartao(next) || cartao(msg);
                    const raio = isOut
                      ? `18px ${primeira ? 18 : 8}px ${ultima ? 5 : 8}px 18px`
                      : `${primeira ? 18 : 8}px 18px 18px ${ultima ? 5 : 8}px`;
                    const viaCelularInicio = isFromPhone && (primeira || (prev.source || '').toUpperCase() !== 'MOBILE_APP');
                    const tipo = tipoDaMidia(msg);
                    const bolhaBase = {
                      position: 'relative', borderRadius: raio, boxShadow: C.sh1,
                      bgcolor: isOut ? C.ob : C.card,
                      border: `1px solid ${isOut ? C.obB : C.bdr}`,
                      color: isOut ? C.obT : C.text,
                      fontFamily: FONT_TXT, fontSize: { xs: 15, md: 14.5 }, lineHeight: 1.45,
                      maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere',
                    };
                    const metaNoCanto = (
                      <Box component="span" sx={{ position: 'absolute', right: 10, bottom: 5 }}>
                        {renderMeta(msg, isOut)}
                      </Box>
                    );
                    let bolha;
                    if (ehFormulario) {
                      bolha = renderFormulario(msg, isOut, raio);
                    } else if (isTmpl && isOut) {
                      bolha = (
                        <Box sx={{
                          width: { xs: 290, md: 340 }, maxWidth: '100%', p: '12px 14px 10px',
                          borderRadius: raio, bgcolor: C.card, border: `1px solid ${C.purpB}`, boxShadow: C.sh1,
                          fontFamily: FONT_TXT, fontSize: { xs: 15, md: 14.5 }, color: C.text, overflowWrap: 'anywhere',
                        }}>
                          <Typography sx={{
                            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                            textTransform: 'uppercase', color: C.purpD, mb: 1,
                            fontFamily: FONT_TXT, display: 'flex', alignItems: 'center', gap: '6px',
                          }}>
                            <Box component="span" aria-hidden sx={{ fontSize: 13, letterSpacing: 0 }}>⬡</Box>
                            Template aprovado
                          </Typography>
                          {renderTemplateFiel(msg, renderMeta(msg, false))}
                        </Box>
                      );
                    } else if (msg.contatos?.length) {
                      // Cartão de contato (nov/26): de quem é, os números e
                      // "Conversar" — antes era só "Contato compartilhado".
                      bolha = (
                        <Box sx={{ ...bolhaBase, p: '10px 12px 24px' }}>
                          {renderTopoDaBolha(msg, isOut)}
                          <CartaoContato contatos={msg.contatos} enviada={isOut}
                            onConversar={({ nome, telefone }) => {
                              setNewClient({ name: nome, phone: telefone });
                              setOpenNewConvModal(true);
                            }} />
                          {metaNoCanto}
                        </Box>
                      );
                    } else if (tipo === 'imagem' || tipo === 'video') {
                      bolha = (
                        <Box sx={{ ...bolhaBase, p: '4px' }}>
                          {(msg.forwarded || msg.citacao) && (
                            <Box sx={{ p: '4px 4px 0' }}>{renderTopoDaBolha(msg, isOut)}</Box>
                          )}
                          {renderMessageContent(msg, isOut)}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '5px 6px 1px' }}>
                            {renderMeta(msg, isOut)}
                          </Box>
                        </Box>
                      );
                    } else if (tipo === 'audio') {
                      bolha = (
                        <Box sx={{ ...bolhaBase, p: '8px 14px 22px 8px' }}>
                          {renderTopoDaBolha(msg, isOut)}
                          {renderMessageContent(msg, isOut)}
                          {metaNoCanto}
                        </Box>
                      );
                    } else if (tipo === 'arquivo') {
                      bolha = (
                        <Box sx={{ ...bolhaBase, p: '10px 12px 24px 10px' }}>
                          {renderTopoDaBolha(msg, isOut)}
                          {renderMessageContent(msg, isOut)}
                          {metaNoCanto}
                        </Box>
                      );
                    } else {
                      // Texto: a hora fica presa no canto de baixo e um
                      // espaço invisível no fim da última linha reserva o
                      // lugar dela — o mesmo truque do WhatsApp.
                      bolha = (
                        <Box sx={{ ...bolhaBase, p: '8px 12px 8px 14px' }}>
                          {renderTopoDaBolha(msg, isOut)}
                          {renderMessageContent(msg, isOut)}
                          <Box component="span" aria-hidden sx={{ display: 'inline-block', width: isOut ? 58 : 40, height: 10 }} />
                          {metaNoCanto}
                        </Box>
                      );
                    }
                    const hoje = new Date();
                    const ontem = new Date(Date.now() - 864e5);
                    const rotuloDia = dia(msg) === hoje.toDateString() ? 'Hoje'
                      : dia(msg) === ontem.toDateString() ? 'Ontem'
                      : format(new Date(msg.timestamp), "EEEE, dd 'de' MMMM", { locale: ptBR });
                    return (
                      <React.Fragment key={msg.id || i}>
                        {!sameDayPrev && (
                          <Box sx={{
                            alignSelf: 'center', m: i === 0 ? '4px 0 6px' : '18px 0 6px',
                            height: 26, px: '12px', display: 'flex', alignItems: 'center',
                            borderRadius: 999, bgcolor: C.card, border: `1px solid ${C.bdr}`,
                            fontFamily: FONT_TXT, fontSize: 12, fontWeight: 600, color: C.text2,
                            '&::first-letter': { textTransform: 'uppercase' },
                          }}>
                            {rotuloDia}
                          </Box>
                        )}
                        <Box id={`msg-${msg.id}`} sx={{
                          display: 'flex', alignItems: 'flex-end', gap: { xs: '6px', md: '8px' },
                          justifyContent: isOut ? 'flex-end' : 'flex-start',
                          mt: primeira ? '12px' : '3px',
                          borderRadius: '12px',
                          // A citada pisca ao ser alcançada pelo clique na citação.
                          bgcolor: destacada === msg.id ? C.sel : 'transparent',
                          transition: 'background-color 0.4s',
                          // Revela as ações. Hover só: no celular não existe,
                          // e lá fica o "⋯" (e o toque longo na bolha).
                          '&:hover .acoes-bolha': { opacity: 1 },
                        }}>
                          {!isOut && (ultima ? (
                            <Box aria-hidden sx={{
                              width: { xs: 26, md: 28 }, height: { xs: 26, md: 28 }, borderRadius: '50%',
                              bgcolor: selectedChipPalette.bg, color: selectedChipPalette.color,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: { xs: 11, md: 12 }, fontWeight: 700, fontFamily: FONT_TIT, flexShrink: 0,
                            }}>
                              {inicial}
                            </Box>
                          ) : (
                            <Box aria-hidden sx={{ width: { xs: 26, md: 28 }, flexShrink: 0 }} />
                          ))}
                          {/* Gatilho de reagir do lado de DENTRO da conversa
                              (à esquerda nas nossas, à direita nas dele). */}
                          {isOut && renderAcoes(msg)}
                          <Box {...toqueLongoBolha(msg)} sx={{
                            maxWidth: { xs: '80%', md: '68%' }, minWidth: 0,
                            display: 'flex', flexDirection: 'column', alignItems: isOut ? 'flex-end' : 'flex-start',
                            ...(isMobile ? { WebkitTouchCallout: 'none' } : {}),
                          }}>
                            {/* Coexistência: enviada do celular do dono. Em
                                azul, fora da bolha: é sobre a ORIGEM, não o
                                conteúdo. Uma vez por sequência. */}
                            {viaCelularInicio && (
                              <Box title="Enviada pelo app do WhatsApp no celular, fora do painel" sx={{
                                display: 'flex', alignItems: 'center', gap: '4px', m: '0 6px 4px',
                                fontFamily: FONT_TXT, fontSize: 11.5, fontWeight: 500, color: C.blueD,
                              }}>
                                <FiSmartphone size={11} aria-hidden />
                                Enviada pelo celular
                              </Box>
                            )}
                            {bolha}
                            {renderReacoes(msg, isOut)}
                            {renderFalhaEntrega(msg)}
                          </Box>
                          {!isOut && renderAcoes(msg)}
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  </>)}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Caixa de mensagem (out/26, redesign): uma pílula que
                    flutua sobre o fim da conversa — o degradê em cima dela
                    deixa as últimas bolhas sumirem por baixo em vez de
                    baterem numa borda. No computador as ferramentas ficam
                    na pílula; no celular vão pro "+", e a pílula fica só
                    com o texto e o emoji (1f). */}
                <Box sx={{
                  position: 'relative', zIndex: 2, flexShrink: 0, bgcolor: C.bg,
                  p: { xs: '4px 12px calc(12px + env(safe-area-inset-bottom))', md: '2px 24px 18px' },
                  '&::before': {
                    content: '""', position: 'absolute', left: 0, right: 0, top: -24, height: 24,
                    background: `linear-gradient(to top, ${C.bg}, transparent)`, pointerEvents: 'none',
                  },
                }}>
                  {/* "Respondendo a…" (nov/26): a mensagem que o próximo
                      envio cita — texto, mídia, áudio ou arquivo. Esc ou o
                      X soltam a citação. */}
                  {respondendo && (
                    <Box role="status" sx={{
                      display: 'flex', alignItems: 'stretch', gap: '10px', mb: '8px',
                      bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '16px',
                      boxShadow: C.sh1, overflow: 'hidden', pr: '8px',
                    }}>
                      {renderCitacao(citacaoLocal(respondendo, urlDaMidia), false, { naCaixa: true })}
                      <Box component="button" type="button" onClick={() => setRespondendo(null)}
                        aria-label="Cancelar resposta"
                        sx={{
                          width: 32, height: 32, p: 0, border: 0, borderRadius: '50%', alignSelf: 'center', flexShrink: 0,
                          bgcolor: C.inset, color: C.text2, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          '&:hover': { color: C.text },
                          '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
                        }}>
                        <FiX size={15} strokeWidth={2.4} />
                      </Box>
                    </Box>
                  )}
                  {isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '6px' }}>
                      {isRecording ? botaoDescartar : (
                        <Box component="button" type="button" ref={botaoMaisRef}
                          aria-label="Mais opções: templates, formulário, anexo e IA"
                          aria-haspopup="menu" aria-expanded={Boolean(menuMaisAnchor)}
                          data-ai-anchor="true"
                          onClick={(e) => setMenuMaisAnchor(e.currentTarget)}
                          sx={{
                            width: 44, height: 44, p: 0, flexShrink: 0, borderRadius: '50%', cursor: 'pointer',
                            bgcolor: C.card, border: `1px solid ${C.bdr}`, color: C.text, boxShadow: C.sh2,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
                          }}>
                          <FiPlus size={20} />
                        </Box>
                      )}
                      {isRecording ? contadorGravacao : (
                        <Box sx={{
                          flex: 1, minWidth: 0, minHeight: 44, display: 'flex', alignItems: 'flex-end', gap: '4px',
                          p: '3px 4px 3px 8px', bgcolor: C.card, border: `1px solid ${C.bdr}`,
                          borderRadius: '22px', boxShadow: C.sh2,
                          transition: 'border-color 0.2s', '&:focus-within': { borderColor: C.greenB },
                        }}>
                          {campoTexto}
                          <Box component="button" type="button"
                            aria-label="Inserir emoji" aria-expanded={!!emojiAnchor}
                            onClick={e => setEmojiAnchor(emojiAnchor ? null : e.currentTarget)}
                            sx={ferramentaSx(C.text2, C.inset, Boolean(emojiAnchor))}>
                            <FiSmile size={19} />
                          </Box>
                        </Box>
                      )}
                      {inputText.trim() ? botaoEnviar : isRecording ? botaoEnviarGravacao : botaoGravar}
                    </Box>
                  ) : (
                    <Box sx={{
                      display: 'flex', alignItems: 'flex-end', gap: '2px', p: '6px',
                      bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '26px', boxShadow: C.sh2,
                      transition: 'border-color 0.2s', '&:focus-within': { borderColor: C.greenB },
                    }}>
                      <Tooltip title="Templates aprovados">
                        <Box component="button" type="button" aria-label="Templates aprovados"
                          aria-pressed={showTmplPanel}
                          onClick={() => setShowTmplPanel(true)}
                          sx={{ ...ferramentaSx(C.purple, C.purpBg, showTmplPanel), fontSize: 16 }}>
                          <Box component="span" aria-hidden>⬡</Box>
                        </Box>
                      </Tooltip>
                      {/* Formulários PR 2 (out/26). Qualquer um que atende
                          envia; montar e publicar é do supervisor. */}
                      <Tooltip title="Enviar formulário">
                        <Box component="button" type="button" aria-label="Enviar formulário"
                          aria-haspopup="dialog" onClick={() => setFormOpen(true)}
                          sx={ferramentaSx(formOpen ? C.blue : C.text2, formOpen ? C.blueBg : C.inset, formOpen)}>
                          <FiClipboard size={17} />
                        </Box>
                      </Tooltip>
                      {/* Aberto = "pressionado", em cinza: o roxo nesse
                          rodapé é de template/IA, e emprestá-lo pro emoji
                          desfaria a associação. */}
                      <Tooltip title="Emoji">
                        <Box component="button" type="button" aria-label="Inserir emoji"
                          aria-expanded={!!emojiAnchor}
                          onClick={e => setEmojiAnchor(emojiAnchor ? null : e.currentTarget)}
                          sx={ferramentaSx(C.text2, C.inset, Boolean(emojiAnchor))}>
                          <FiSmile size={17} />
                        </Box>
                      </Tooltip>
                      <Tooltip title="Anexar arquivo">
                        <Box component="button" type="button" aria-label="Anexar arquivo"
                          onClick={() => fileInputRef.current?.click()} sx={ferramentaSx()}>
                          <FiPaperclip size={17} />
                        </Box>
                      </Tooltip>
                      {aiEnabled && (
                        <Tooltip title={messages.length === 0 ? 'Sem histórico para a IA ler' : 'Sugerir resposta com IA'}>
                          {/* span: o Tooltip precisa de um filho que receba
                              eventos mesmo com o botão desabilitado */}
                          <span>
                            <Box component="button" type="button"
                              aria-label="Sugerir resposta com IA"
                              aria-expanded={aiCardOpen}
                              data-ai-anchor="true"
                              disabled={messages.length === 0}
                              onClick={handleToggleAiCard}
                              sx={ferramentaSx(C.purple, C.purpBg, aiCardOpen)}>
                              <FiCpu size={17} />
                            </Box>
                          </span>
                        </Tooltip>
                      )}
                      {campoTexto}
                      {inputText.trim() ? botaoEnviar : isRecording ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {botaoDescartar}
                          {contadorGravacao}
                          {botaoEnviarGravacao}
                        </Box>
                      ) : botaoGravar}
                    </Box>
                  )}
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }}
                    onChange={handleFileUpload} accept="image/*,video/*,application/pdf" />

                  {/* "+" do celular: as ferramentas que no computador ficam
                      na pílula. A IA abre o mesmo cartão, ancorado no "+". */}
                  <Menu
                    anchorEl={menuMaisAnchor}
                    open={Boolean(menuMaisAnchor)}
                    onClose={() => setMenuMaisAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    slotProps={{ paper: { sx: {
                      mt: '-8px', minWidth: 240, borderRadius: '18px', bgcolor: C.card, color: C.text,
                      border: `1px solid ${C.bdr}`, boxShadow: C.sh2, backgroundImage: 'none',
                    } } }}
                  >
                    {[
                      { chave: 'tpl', rotulo: 'Templates aprovados', icone: <Box component="span" sx={{ fontSize: 16 }}>⬡</Box>, cor: C.purple, fundo: C.purpBg,
                        acao: () => setShowTmplPanel(true) },
                      { chave: 'form', rotulo: 'Enviar formulário', icone: <FiClipboard size={17} />, cor: C.blue, fundo: C.blueBg,
                        acao: () => setFormOpen(true) },
                      { chave: 'anexo', rotulo: 'Anexar arquivo', icone: <FiPaperclip size={17} />, cor: C.text2, fundo: C.inset,
                        acao: () => fileInputRef.current?.click() },
                      ...(aiEnabled ? [{ chave: 'ia', rotulo: 'Sugerir resposta com IA', icone: <FiCpu size={17} />, cor: C.purple, fundo: C.purpBg,
                        desabilitado: messages.length === 0,
                        acao: () => handleToggleAiCard({ currentTarget: botaoMaisRef.current }) }] : []),
                    ].map(item => (
                      <MenuItem key={item.chave} disabled={item.desabilitado}
                        onClick={() => { setMenuMaisAnchor(null); item.acao(); }}
                        sx={{ minHeight: 48, gap: '12px', fontFamily: FONT_TXT, fontSize: 15, color: C.text,
                          '&:hover': { bgcolor: C.hover }, '&.Mui-focusVisible': { bgcolor: C.hover } }}>
                        <Box component="span" aria-hidden sx={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: item.fundo, color: item.cor,
                        }}>
                          {item.icone}
                        </Box>
                        {item.rotulo}
                      </MenuItem>
                    ))}
                  </Menu>

                  {/* Card de sugestão — position: fixed, ancorado no botão
                      de IA. Fica aqui pra viver e morrer junto do rodapé. */}
                  <AISuggestionCard
                    open={aiCardOpen}
                    onClose={() => setAiCardOpen(false)}
                    clientId={selectedClient?.id}
                    anchorRect={aiAnchorRect}
                    onUseText={handleAiUseText}
                    onEditText={handleAiEditText}
                  />
                </Box>
              </>
              );
            })() : (
              /* Nenhuma conversa aberta (só no computador: no celular a
                 lista ocupa a tela). */
              <Box sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: '10px', p: 4, textAlign: 'center',
              }}>
                <Box sx={{
                  width: 72, height: 72, borderRadius: '50%', mb: 1,
                  bgcolor: C.greenBg, border: `1px solid ${C.greenB}`, color: C.greenD,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FiMessageSquare size={28} aria-hidden />
                </Box>
                <Typography component="h2" sx={{ m: 0, fontSize: 20, fontWeight: 700, color: C.text, fontFamily: FONT_TIT }}>
                  Selecione uma conversa
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: C.text2, fontFamily: FONT_TXT, maxWidth: 340 }}>
                  Escolha uma conversa na lista ao lado ou comece uma nova.
                </Typography>
                <Box component="button" type="button"
                  onClick={() => setOpenNewConvModal(true)}
                  sx={{
                    mt: 1.5, height: 44, px: '20px', border: 0, borderRadius: 999, cursor: 'pointer',
                    bgcolor: C.green, color: C.sobreVerde, fontFamily: FONT_TXT, fontSize: 14.5, fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    boxShadow: '0 10px 24px -12px rgba(0,160,95,0.6)',
                    '&:hover': { bgcolor: 'var(--ch-green-hover)' },
                    '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 3 },
                  }}
                >
                  <FiPlus size={16} aria-hidden /> Iniciar nova conversa
                </Box>
              </Box>
            )}
          </Box>

          {/* ──────────── COL DIREITA ────────────
              Uma coluna, dois donos: o painel de templates (envio avulso
              ou em massa) e a mini-página do contato. Templates têm
              prioridade — quem pediu um template está no meio de um envio.
              No celular os dois sobem de baixo como folha (out/26: antes
              o painel de templates simplesmente não aparecia no celular, e
              o botão ⬡ não fazia nada por lá). */}
          {!isMobile && showTmplPanel && (
            <Box component="aside" aria-label="Templates" sx={{
              width: 340, minWidth: 340, bgcolor: C.card,
              borderLeft: `1px solid ${C.bdr}`,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              {renderPainelTemplates(false)}
            </Box>
          )}
          {!isMobile && !showTmplPanel && contatoAberto && clienteAberto && (
            <PainelContato
              modo="coluna"
              cliente={clienteAberto}
              avatar={selectedChipPalette}
              onFechar={() => alternarContato(false)}
              onAbrirCrm={(id) => navigate(`/contatos?selected=${id}`)}
              etiquetas={etiquetasDoPainel}
            />
          )}
        </Box>
      </Box>

      {isMobile && (
        <Drawer
          anchor="bottom" open={showTmplPanel} onClose={fecharPainelTemplates}
          slotProps={{ backdrop: { sx: { bgcolor: C.veu } } }}
          PaperProps={{
            'aria-label': selectedConvIds.size > 0 ? 'Envio em massa' : 'Templates aprovados',
            // Altura do conteúdo, até 88% da tela: com 1 ou 2 templates a
            // folha não sobe vazia até o topo.
            sx: {
              maxHeight: '88dvh', bgcolor: C.card, color: C.text, backgroundImage: 'none',
              borderRadius: '28px 28px 0 0', boxShadow: '0 -20px 50px -10px rgba(0,0,0,0.5)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: FONT_TXT,
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '8px', flexShrink: 0 }}>
            <Box component="span" sx={{ width: 36, height: 5, borderRadius: 999, bgcolor: C.bdr2 }} />
          </Box>
          {renderPainelTemplates(true)}
        </Drawer>
      )}
      {isMobile && clienteAberto && (
        <PainelContato
          modo="folha"
          aberto={contatoFolha}
          cliente={clienteAberto}
          avatar={selectedChipPalette}
          onFechar={() => setContatoFolha(false)}
          onAbrirCrm={(id) => navigate(`/contatos?selected=${id}`)}
          etiquetas={etiquetasDoPainel}
        />
      )}

      {/* Formulários PR 2: "Enviar formulário". A janela calculada aqui
          (último INBOUND nas 24h) só serve pra avisar antes; o servidor
          confere de novo e devolve 409 com a frase. */}
      {selectedClient && (
        <EnviarFormulario
          open={formOpen}
          onClose={() => setFormOpen(false)}
          deviceId={deviceId}
          client={selectedClient}
          janelaAberta={(() => {
            const ult = [...messages].reverse().find((m) => m.direction === 'INBOUND');
            return Boolean(ult && Date.now() - new Date(ult.timestamp).getTime() < 864e5);
          })()}
          onEnviado={() => {
            setFormOpen(false);
            if (fetchMessagesRef.current) fetchMessagesRef.current();
            if (fetchClientsRef.current) fetchClientsRef.current();
          }}
          // PR 3: fora da janela, o formulário vai num template com o botão
          // dele — pelo mesmo envio do painel de templates (variáveis,
          // crédito, teto). O diálogo fecha antes pra o de variáveis, se
          // precisar, aparecer por cima.
          templates={templates}
          onEnviarTemplate={async (tpl) => { setFormOpen(false); await handleSendTemplateSingle(tpl); }}
          podeCriarTemplate={['SUPERVISOR', 'ADMIN'].includes(user?.role)}
        />
      )}

      {/* ─── Encaminhar (nov/26) ─── */}
      <EncaminharDialog
        open={Boolean(encaminhando)}
        onClose={() => setEncaminhando(null)}
        mensagem={encaminhando}
        deviceId={deviceId}
        conversas={clients}
        isMobile={isMobile}
        onEncaminhado={(r) => {
          if (r?.enviados) {
            setAviso(`Encaminhada para ${r.enviados} conversa${r.enviados !== 1 ? 's' : ''}.`);
            if (fetchClientsRef.current) fetchClientsRef.current();
            if ((r.resultados || []).some(x => x.ok && x.client_id === selectedClient?.id) && fetchMessagesRef.current) {
              fetchMessagesRef.current();
            }
          }
        }}
      />

      {/* Ações da mensagem no celular: o "⋯" da bolha ou o toque longo. */}
      <Drawer
        anchor="bottom" open={Boolean(acoesMsg)} onClose={() => setAcoesMsg(null)}
        slotProps={{ backdrop: { sx: { bgcolor: C.veu } } }}
        PaperProps={{
          'aria-label': 'Ações da mensagem',
          sx: {
            bgcolor: C.card, color: C.text, backgroundImage: 'none', borderRadius: '24px 24px 0 0',
            p: '8px 12px calc(14px + env(safe-area-inset-bottom))', fontFamily: FONT_TXT,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: '10px' }}>
          <Box component="span" sx={{ width: 36, height: 5, borderRadius: 999, bgcolor: C.bdr2 }} />
        </Box>
        {acoesMsg && (
          <>
            <Box sx={{
              mx: '8px', mb: '8px', p: '10px 12px', borderRadius: '12px', bgcolor: C.surf, border: `1px solid ${C.bdr}`,
              fontSize: 14, color: C.text2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {resumoDaMensagem(acoesMsg.msg, 200)}
            </Box>
            {[
              podeResponder(acoesMsg.msg) && { chave: 'responder', rotulo: 'Responder', icone: <FiCornerUpLeft size={18} />,
                acao: () => iniciarResposta(acoesMsg.msg) },
              podeEncaminhar(acoesMsg.msg) && { chave: 'encaminhar', rotulo: 'Encaminhar', icone: <FiCornerUpRight size={18} />,
                acao: () => setEncaminhando(acoesMsg.msg) },
              acoesMsg.msg?.whatsapp_message_id && { chave: 'reagir', rotulo: 'Reagir', icone: <FiSmile size={18} />,
                acao: () => setReacaoAlvo({ msg: acoesMsg.msg, anchorEl: acoesMsg.anchorEl }) },
            ].filter(Boolean).map(item => (
              <Box key={item.chave} component="button" type="button"
                onClick={() => { setAcoesMsg(null); item.acao(); }}
                sx={{
                  width: '100%', minHeight: 52, display: 'flex', alignItems: 'center', gap: '14px', px: '14px',
                  border: 0, borderRadius: '12px', bgcolor: 'transparent', color: C.text, cursor: 'pointer',
                  font: 'inherit', fontSize: 16, textAlign: 'left',
                  '&:hover, &:active': { bgcolor: C.hover },
                  '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: -2 },
                }}>
                <Box component="span" aria-hidden sx={{
                  width: 36, height: 36, borderRadius: '50%', bgcolor: C.inset, color: C.text2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {item.icone}
                </Box>
                {item.rotulo}
              </Box>
            ))}
          </>
        )}
      </Drawer>

      <Snackbar
        open={Boolean(aviso)} autoHideDuration={4500} onClose={() => setAviso('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={aviso}
        ContentProps={{ sx: {
          bgcolor: C.text, color: C.bg, fontFamily: FONT_TXT, fontSize: 14, borderRadius: '14px', boxShadow: C.sh2,
          mb: { xs: '84px', md: '96px' },
        } }}
      />

      {/* Modal: Bulk delete confirm */}
      <Dialog open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: dialogoPapelSx }}>
        <DialogTitle sx={dialogoTituloSx}>Remover conversas selecionadas?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: C.text2, fontFamily: FONT_TXT, mb: 1.5 }}>
            {selectedConvIds.size} conversa{selectedConvIds.size !== 1 ? 's' : ''} será{selectedConvIds.size !== 1 ? 'ão' : ''} removida{selectedConvIds.size !== 1 ? 's' : ''} da listagem.
          </Typography>
          <Box sx={{
            p: '10px 12px', borderRadius: '12px', fontFamily: FONT_TXT, fontSize: 13.5, lineHeight: 1.5,
            bgcolor: C.blueBg, border: `1px solid ${C.blueB}`, color: C.blueD,
          }}>
            O contato permanece em <b>Contatos/CRM</b> com todo o histórico (notas, etiquetas, status).
            Se chegar uma nova mensagem depois, a conversa reaparece automaticamente aqui.
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '8px 20px 20px', gap: 1 }}>
          <Button onClick={() => setBulkDeleteConfirm(false)} sx={dialogoBotaoSx('neutro')}>Cancelar</Button>
          <Button onClick={handleBulkDelete} sx={dialogoBotaoSx('perigo')} startIcon={<FiTrash2 size={14} />}>
            Remover {selectedConvIds.size}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal: Nova conversa */}
      <Dialog open={openNewConvModal} onClose={() => setOpenNewConvModal(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: dialogoPapelSx }}>
        <DialogTitle sx={dialogoTituloSx}>Iniciar nova conversa</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Nome do contato" value={newClient.name}
            onChange={e => setNewClient({ ...newClient, name: e.target.value })} sx={{ ...campoDialogoSx, mb: 2, mt: 1 }} />
          <TextField fullWidth label="Número (com código do país)" placeholder="5521999999999"
            inputMode="tel"
            value={newClient.phone} onChange={e => setNewClient({ ...newClient, phone: e.target.value })} sx={{ ...campoDialogoSx, mb: 1 }} />
          {newConvError && (
            <Box role="alert" sx={{
              mt: 1, p: '10px 12px', borderRadius: '12px', fontFamily: FONT_TXT, fontSize: 13.5, lineHeight: 1.5,
              bgcolor: C.redBg, border: `1px solid ${C.redB}`, color: C.redD,
            }}>
              {newConvError}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '8px 20px 20px', gap: 1 }}>
          <Button onClick={() => setOpenNewConvModal(false)} disabled={newConvLoading}
            sx={dialogoBotaoSx('neutro')}>Cancelar</Button>
          <Button onClick={handleStartNewConv} disabled={newConvLoading} sx={dialogoBotaoSx('verde')}>
            {newConvLoading ? 'Criando…' : 'Iniciar conversa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Valores que faltam pra enviar um template com variável. Só abre
          quando o servidor diz que falta algo — ver handleSendTemplateSingle. */}
      {/* Limpeza em lote das conversas antigas (out/26). Abre pelo aviso
          que aparece na lista quando o chip passa do limiar. */}
      <LimpezaConversasDialog
        aberto={limpezaAberta}
        onFechar={() => setLimpezaAberta(false)}
        deviceId={deviceId}
        total={totalConversas}
        carregadas={clients.length}
        onLimpou={(quantas) => {
          alert(`${quantas.toLocaleString('pt-BR')} conversas foram limpas do chat. `
                + 'Os contatos continuam em Contatos.');
          fetchClients();
        }}
      />


      <TemplateVarsDialog
        open={Boolean(varsPrompt)}
        templateName={varsPrompt?.template?.name}
        contactName={selectedClient?.name}
        missing={varsPrompt?.missing || []}
        preview={varsPrompt?.preview || ''}
        sending={sendingVars}
        onClose={() => setVarsPrompt(null)}
        onConfirm={handleConfirmVars}
      />

      {/* ─── Emoji (out/26) ──────────────────────────────────────────────
          Duas instâncias do MESMO painel, e não uma com âncora trocada:
          cada uma tem seu estado de aba e de busca, então abrir o de
          reagir não embaralha a busca que ficou pela metade no rodapé.

          O de escrever NÃO fecha ao escolher — quem está montando uma
          frase costuma querer dois ou três emojis seguidos, e reabrir o
          painel a cada um seria trabalho à toa. O de reagir fecha, porque
          só existe uma reação por mensagem e a escolha encerra o gesto. */}
      <PainelEmoji
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        onEscolher={inserirEmoji}
      />

      <PainelEmoji
        open={Boolean(reacaoAlvo)}
        anchorEl={reacaoAlvo?.anchorEl || null}
        onClose={() => setReacaoAlvo(null)}
        modo="reacao"
        atual={msgEmReacao?.reaction_out || null}
        onEscolher={(emoji) => reagir(msgEmReacao, emoji)}
      />
    </Box>
    </ThemeProvider>
  );
}

// ─── Sub-componentes auxiliares ──────────────────────────────────────────────

function BulkBtn({ children, onClick, sx }) {
  return (
    <Box component="button" type="button" onClick={onClick} sx={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      height: 30, px: 1.3, borderRadius: 999,
      fontFamily: FONT_TXT, fontSize: 12.5, fontWeight: 600,
      cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
      transition: 'filter 0.15s',
      '&:hover': { filter: 'brightness(1.08)' },
      '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
      ...sx,
    }}>
      {children}
    </Box>
  );
}

// Seleção de conversa: círculo (out/26, redesign), não mais o quadradinho.
// É um botão de verdade com `aria-pressed`, pra o teclado e o leitor de tela.
function CheckBox({ state, onClick, rotulo }) {
  // state: 'none' | 'partial' | 'all'
  const isChecked = state === 'all';
  const isPartial = state === 'partial';
  return (
    <Box component="button" type="button" onClick={onClick}
      aria-pressed={isChecked ? 'true' : isPartial ? 'mixed' : 'false'}
      aria-label={rotulo}
      sx={{
        width: 18, height: 18, p: 0, borderRadius: '50%',
        border: `1.5px solid ${isChecked || isPartial ? C.green : C.bdr2}`,
        bgcolor: isChecked ? C.green : isPartial ? C.greenBg : 'transparent',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, transition: 'background-color 0.15s, border-color 0.15s',
        color: isChecked ? C.sobreVerde : C.greenD,
        '&:hover': { borderColor: C.green },
        '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
      }}>
      {isChecked ? <FiCheck size={11} strokeWidth={3.2} /> : isPartial
        ? <Box component="span" sx={{ width: 8, height: 2, borderRadius: 1, bgcolor: 'currentColor' }} />
        : null}
    </Box>
  );
}

// Diálogos do chat no tema (out/26, redesign): o MUI pinta o papel de
// branco e os campos de cinza-escuro — no escuro, um clarão. Valem por
// token, então acompanham o tema sem saber qual está ligado.
const dialogoPapelSx = {
  bgcolor: C.card, color: C.text, backgroundImage: 'none',
  border: `1px solid ${C.bdr}`, borderRadius: '20px', boxShadow: C.sh2,
};
const dialogoTituloSx = { fontFamily: FONT_TIT, fontWeight: 700, fontSize: 18, color: C.text, pb: 1 };
const dialogoBotaoSx = (tom) => ({
  textTransform: 'none', fontFamily: FONT_TXT, fontSize: 14, fontWeight: 600,
  height: 40, px: '18px', borderRadius: 999,
  ...(tom === 'verde' ? {
    bgcolor: C.green, color: C.sobreVerde, '&:hover': { bgcolor: 'var(--ch-green-hover)' },
  } : tom === 'perigo' ? {
    bgcolor: C.red, color: '#fff', '&:hover': { bgcolor: C.red, filter: 'brightness(1.08)' },
  } : {
    color: C.text2, '&:hover': { bgcolor: C.inset, color: C.text },
  }),
  '&.Mui-disabled': { opacity: 0.55, color: tom === 'neutro' ? C.cap : undefined },
});
const campoDialogoSx = {
  '& .MuiInputLabel-root': { color: C.cap, fontFamily: FONT_TXT },
  '& .MuiInputLabel-root.Mui-focused': { color: C.greenD },
  '& .MuiOutlinedInput-root': {
    color: C.text, fontFamily: FONT_TXT, borderRadius: '12px',
    '& fieldset': { borderColor: C.bdr2 },
    '&:hover fieldset': { borderColor: C.cap },
    '&.Mui-focused fieldset': { borderColor: C.green },
  },
  '& input::placeholder': { color: C.cap, opacity: 1 },
};

// ─── ETIQUETAS (labels) ─────────────────────────────────────────────────────

// Map de cor → tokens da paleta. Single source pra evitar drift entre as
// 3 superfícies que renderizam labels (conv item, chat header, popover).
const LABEL_COLOR_MAP = {
  green:  { bg: C.greenBg, color: C.greenD,  border: C.greenB,  dot: C.green  },
  blue:   { bg: C.blueBg,  color: C.blueD,   border: C.blueB,   dot: C.blue   },
  purple: { bg: C.purpBg,  color: C.purpD,   border: C.purpB,   dot: C.purple },
  red:    { bg: C.redBg,   color: C.redD,    border: C.redB,    dot: C.red    },
  amber:  { bg: C.amberBg, color: C.amberD,  border: C.amberB,  dot: C.amber  },
  pink:   { bg: C.pinkBg,  color: C.pinkD,   border: C.pinkB,   dot: C.pink   },
};
const VALID_LABEL_COLORS = Object.keys(LABEL_COLOR_MAP);

// Status CRM (INTEREST_LEVELS do backend) — usado pelo dropdown
// "Interesse". Espelha o mesmo vocabulário exibido em /contatos, em pt-BR.
//
// Out/26: este mapa alimentava um chip rotulado "Pastas", que por isso
// nunca mostrava pasta nenhuma — o agente abria "Pastas" e via
// "Alto interesse / Médio interesse / ...". São eixos diferentes: pasta é
// ONDE o contato foi guardado, interesse é O QUANTO ele está quente, e um
// contato tem os dois ao mesmo tempo. Agora cada um tem o seu chip.
const INTEREST_LABEL = {
  ALTO:  'Alto interesse',
  MEDIO: 'Médio interesse',
  BAIXO: 'Baixo interesse',
  FRIO:  'Frio',
};
const INTEREST_ORDER = ['ALTO', 'MEDIO', 'BAIXO', 'FRIO'];
const INTEREST_DOT = {
  ALTO:  C.blue,   // alto engajamento = azul
  MEDIO: C.amber,
  BAIXO: C.text3,
  FRIO:  C.red,
};

// Cor da pasta no dropdown. Valores IGUAIS aos de `FOLDER_PALETTE.tab` em
// UserAdmin/Contacts — a mesma pasta precisa ter a mesma cor nas duas
// telas, senão "Academia" é verde no CRM e azul no chat.
//
// Literais em vez de import: o tema do ChatPage (C) é próprio e não
// expõe essa paleta, e importar da página de Contatos criaria uma
// dependência entre duas telas que não se conhecem. O acoplamento é este
// comentário — se a paleta de lá mudar, esta precisa mudar junto.
//
// Quadrado (não círculo, como o interesse) porque é o mesmo par de formas
// que o CRM usa pra separar os dois eixos.
const FOLDER_DOT = {
  blue:   '#3b82f6',
  green:  '#10b981',
  amber:  '#f59e0b',
  purple: '#a855f7',
  pink:   '#ec4899',
  red:    '#ef4444',
};

// Pill colorida de uma etiqueta atribuída. Clique = remover (toggle).
// `grande`: a versão da mini-página do contato (28 px, como no design).
function LabelPill({ label, onClick, grande = false }) {
  const c = LABEL_COLOR_MAP[label.color] || LABEL_COLOR_MAP.green;
  return (
    <Box
      component="button" type="button"
      onClick={onClick}
      title="Clique para remover"
      aria-label={`Etiqueta ${label.name}. Remover`}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: grande ? '6px' : '5px',
        height: grande ? 28 : 20, px: grande ? '11px' : '8px', borderRadius: 999,
        fontSize: grande ? 13 : 11.5, fontWeight: 500, fontFamily: FONT_TXT,
        bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`,
        cursor: 'pointer', transition: 'opacity 0.15s',
        '&:hover': { opacity: 0.7 },
        '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
      }}
    >
      <Box component="span" sx={{ width: grande ? 6 : 5, height: grande ? 6 : 5, borderRadius: '50%', bgcolor: 'currentColor' }} />
      {label.name}
    </Box>
  );
}

// Chip "+ etiqueta" — tracejado, abre o popover.
function AddLabelChip({ onClick, grande = false }) {
  return (
    <Box
      component="button" type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.3,
        height: grande ? 28 : 20, px: grande ? '11px' : '8px', borderRadius: 999,
        fontSize: grande ? 13 : 11.5, fontWeight: 500, fontFamily: FONT_TXT,
        color: C.cap, border: `1px dashed ${C.bdr2}`, bgcolor: 'transparent',
        cursor: 'pointer', transition: 'all 0.15s',
        '&:hover': { color: C.text2, borderColor: C.green, bgcolor: C.greenBg },
        '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
      }}
    >
      + etiqueta
    </Box>
  );
}

// Popover absoluto que abre embaixo do item, com busca + lista + criar.
// É renderizado dentro do próprio conv item (que tem position:relative),
// então fica naturalmente ancorado. O onClick={stopPropagation} no
// container evita que cliques dentro do popover disparem o select da conv.
function LabelPopover({
  client, allLabels, labelSearch, setLabelSearch,
  creatingLabel, setCreatingLabel,
  newLabelColor, setNewLabelColor,
  onToggle, onCreate, onDelete, onClose,
  esquerda = 62,
}) {
  const assignedIds = new Set((client.labels || []).map(l => l.id));
  const filtered = allLabels.filter(l =>
    l.name.toLowerCase().includes(labelSearch.toLowerCase())
  );
  const exactMatch = filtered.some(l => l.name.toLowerCase() === labelSearch.toLowerCase().trim());
  const showCreatePrompt = labelSearch.trim() && !exactMatch;

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute', top: '100%', left: `${esquerda}px`,
        zIndex: 100, mt: 0.5,
        bgcolor: C.card, border: `1px solid ${C.bdr}`, color: C.text,
        borderRadius: '16px', p: '12px 12px 10px', width: 260,
        boxShadow: C.sh2, textAlign: 'left',
        animation: 'lblPopIn 0.15s ease',
        '@keyframes lblPopIn': {
          from: { opacity: 0, transform: 'translateY(-4px)' },
          to:   { opacity: 1, transform: 'translateY(0)' },
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 600, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: C.cap, fontFamily: FONT_TXT,
          flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pl: '4px',
        }}>
          Etiquetas · {(client.name || client.phone_number || '').slice(0, 20)}
        </Typography>
        <Box
          component="button" type="button"
          onClick={onClose}
          aria-label="Fechar etiquetas"
          sx={{
            width: 26, height: 26, p: 0, border: 0, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: C.text2, bgcolor: C.inset,
            '&:hover': { color: C.text },
            '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
          }}
        >
          <FiX size={13} strokeWidth={2.4} />
        </Box>
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '8px',
        bgcolor: C.inset, border: '1px solid transparent',
        borderRadius: '10px', px: '10px', height: 36, mb: 1,
        '&:focus-within': { borderColor: C.greenB },
      }}>
        <FiSearch size={14} color="var(--ch-cap)" aria-hidden />
        <Box component="input"
          autoFocus
          aria-label="Buscar ou criar etiqueta"
          placeholder="Buscar ou criar…"
          value={labelSearch}
          onChange={e => setLabelSearch(e.target.value)}
          sx={{
            background: 'none', border: 'none', outline: 'none',
            fontFamily: FONT_TXT, fontSize: 14, color: C.text,
            width: '100%',
            '&::placeholder': { color: C.cap, opacity: 1 },
          }}
        />
      </Box>

      {/* Lista de etiquetas existentes (filtrada pela busca) */}
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: 0.3,
        maxHeight: 180, overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { background: C.bdr2, borderRadius: 3 },
      }}>
        {filtered.length === 0 && !showCreatePrompt && (
          <Typography sx={{
            fontSize: 13, color: C.cap, textAlign: 'center', py: 1, fontFamily: FONT_TXT,
          }}>
            Nenhuma etiqueta. Digite acima pra criar.
          </Typography>
        )}
        {filtered.map(lbl => {
          const isAssigned = assignedIds.has(lbl.id);
          const c = LABEL_COLOR_MAP[lbl.color] || LABEL_COLOR_MAP.green;
          return (
            <Box key={lbl.id}
              role="menuitemcheckbox" aria-checked={isAssigned} tabIndex={0}
              onClick={() => onToggle(lbl.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(lbl.id); } }}
              sx={{
                display: 'flex', alignItems: 'center', gap: '10px',
                px: '8px', minHeight: 36, borderRadius: '10px', cursor: 'pointer',
                bgcolor: isAssigned ? c.bg : 'transparent',
                '&:hover': { bgcolor: isAssigned ? c.bg : C.hover },
                '&:hover .del-label-btn, &:focus-within .del-label-btn': { opacity: 1 },
                '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: -2 },
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.dot, flexShrink: 0 }} />
              <Typography sx={{
                fontSize: 14, color: isAssigned ? c.color : C.text,
                flex: 1, minWidth: 0, fontFamily: FONT_TXT, fontWeight: isAssigned ? 600 : 400,
              }}>
                {lbl.name}
              </Typography>
              {isAssigned && (
                <Box component="span" sx={{ color: c.color, display: 'flex' }}><FiCheck size={14} strokeWidth={2.8} /></Box>
              )}
              {/* Lixeira: aparece no hover da linha. Só mostra se
                  onDelete foi passado (retrocompat com quem não usa). */}
              {onDelete && (
                <Tooltip title="Excluir etiqueta da empresa" placement="left">
                  <Box
                    className="del-label-btn"
                    onClick={(e) => { e.stopPropagation(); onDelete(lbl.id, lbl.name); }}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 26, height: 26, borderRadius: '8px', cursor: 'pointer',
                      color: C.cap, opacity: 0, transition: 'opacity .12s, color .12s, background .12s',
                      '&:hover': { color: C.red, bgcolor: C.redBg },
                    }}
                  >
                    <FiTrash2 size={13} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Bloco "criar nova" — aparece quando busca não casa exatamente,
          ou quando o usuário clica em "+ Criar nova etiqueta". */}
      {(showCreatePrompt || creatingLabel) && (
        <Box sx={{
          mt: 1, pt: 1, borderTop: `1px solid ${C.bdr}`,
          display: 'flex', flexDirection: 'column', gap: 0.7,
        }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: C.cap, fontFamily: FONT_TXT, pl: '4px',
          }}>
            Criar "{labelSearch.trim() || 'nova etiqueta'}"
          </Typography>
          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', pl: '4px' }}>
            {VALID_LABEL_COLORS.map(col => {
              const c = LABEL_COLOR_MAP[col];
              return (
                <Box key={col}
                  component="button" type="button"
                  onClick={() => setNewLabelColor(col)}
                  aria-label={`Cor ${col}`} aria-pressed={newLabelColor === col}
                  sx={{
                    width: 22, height: 22, p: 0, borderRadius: '50%',
                    bgcolor: c.dot, cursor: 'pointer',
                    border: `2px solid ${newLabelColor === col ? C.text : 'transparent'}`,
                    boxShadow: newLabelColor === col ? `inset 0 0 0 2px ${C.card}` : 'none',
                    '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
                  }}
                />
              );
            })}
          </Box>
          <Box
            component="button"
            onClick={() => onCreate(labelSearch.trim())}
            disabled={!labelSearch.trim()}
            type="button"
            sx={{
              bgcolor: labelSearch.trim() ? C.green : C.inset,
              color: labelSearch.trim() ? C.sobreVerde : C.cap, border: 'none', borderRadius: 999,
              height: 36, fontSize: 13.5, fontWeight: 600, fontFamily: FONT_TXT,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              cursor: labelSearch.trim() ? 'pointer' : 'default',
              '&:hover': labelSearch.trim() && { bgcolor: 'var(--ch-green-hover)' },
              '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
            }}
          >
            <FiPlus size={14} aria-hidden /> Criar etiqueta
          </Box>
        </Box>
      )}

      {!showCreatePrompt && !creatingLabel && (
        <Box
          component="button" type="button"
          onClick={() => setCreatingLabel(true)}
          sx={{
            mt: 1, pt: 1, width: '100%', border: 0, borderTop: `1px solid ${C.bdr}`, bgcolor: 'transparent',
            display: 'flex', alignItems: 'center', gap: '6px', pl: '6px',
            fontSize: 13, color: C.cap, cursor: 'pointer',
            fontFamily: FONT_TXT,
            '&:hover': { color: C.greenD },
            '&:focus-visible': { outline: `2px solid ${C.green}`, outlineOffset: 2 },
          }}
        >
          <FiPlus size={13} aria-hidden /> Criar nova etiqueta
        </Box>
      )}
    </Box>
  );
}
